/* eslint-disable */
import React, { useState, useEffect, useCallback, Fragment } from 'react';
import DropdownFilter from '../utils/DropdownFilter';
import ReportViewPopup from './ReportViewPopup';
import LoaderGraphic from '../utils/LoaderGraphic';
import { Data, Filters } from './contexts';
import { capitalize } from '../utils';
import AccountTree from '../utils/AccountTree';
import { PnlValues, BalanceSheetValues } from './monthly/forecast/ForecastHelpers';
import { LinesChart, ColumnsChart, LineColChart, ColLineChart } from '../utils/SVGIcons';
import { customersFinancialDetailRows, customersKPIsFormat, updateCustomersRows, addMetricRow } from '../utils/CustomerSheets';
import { defaultLayouts, moneyCharts, peopleCharts, customerCharts, mergeItems, layoutMaker, pageCharts } from './charts';
import { SurveyCharts, MoneySurvey, PeopleSurvey, CustomerSurvey } from './SurveyHelper';
import { ToastContainer, toast } from 'react-toastify';
import ahoy from 'ahoy.js';
import DashboardViewModal from './utils/DashboardViewModal';
import Modal from 'react-modal';
import DOMPurify from 'dompurify';
import HighchartsReact from 'highcharts-react-official';
import Highcharts from 'highcharts/highcharts.js';
import CopilotSlideOut from './CopilotSlideOut';

const naicsPNLTitle = (naicsCode, entityType) => {
  if (naicsCode.startsWith('6212')) {
    return 'Dental P&L'
  } else if (naicsCode.startsWith('6211') || naicsCode.startsWith('6213')) {
    return 'Doctor P&L'
  } else {
    return null
  }
}

const filterTabsByConditions = (tabs, userType, planAccess, naicsCode, entityType) => {
  let copilotVisible = planAccess.money.ai_viewable
  let naicsSpecificReports = ['6212', '6211', '6213']

  return tabs.filter(tab => {
    if ((tab.key === 'peerview_copilot' && userType === 'Admin') || (tab.key === 'peerview_copilot' && planAccess.money.ai_viewable)) {
      return tab
    } else if ((tab.key === 'docs_pnl') && (naicsSpecificReports.some(prefix => naicsCode.startsWith(prefix)))) {
      tab.name = naicsPNLTitle(naicsCode, entityType)
      return tab
    } else if (tab.key !== 'peerview_copilot' && (tab.key !== 'docs_pnl' || tab.key !== 'functional_expenses')) {
      return tab
    }
  });
};

const sanitizeOptions = {
  ALLOWED_TAGS: [
    'table',
    'thead',
    'tbody',
    'tfoot',
    'tr',
    'td',
    'th',
    'colgroup',
    'col',
    'h1','h2','h3','h4','h5','h6',
    'br','p','ul','ol','li','hr','div',
    'strong','em','u','s','a','span','mark'
  ],
  ALLOWED_ATTR: [
    'href', 'target', 'rel', 'style', 'dir', 'class', 'colspan', 'rowspan', 'colwidth', 'data-colwidth'
  ]
};

const debounce = (fn, delay) => {
    let timeout;
    return (...args) => {
        clearTimeout(timeout);
        timeout = setTimeout(() => fn(...args), delay);
    };
};

const ReportContainer = (props) => {
  Modal.setAppElement(document.getElementById('reports-container'));
  const navbar = window.navbar.navbar;
  let allTabs = props.report_tabs ? filterTabsByConditions(props.report_tabs?.all_tabs, navbar.user_type, props.planAccess, props.code, props.entity_type) : []
  const [activeTab, setActiveTab] = useState(props.report_tabs.active_view)
  const [reportsOnTabs, setReportsOnTabs] = useState(allTabs.filter(v => v.display_on_tab).sort((a, b) => a.tab_order - b.tab_order));
  const [allReportTabs, setAllReportTabs] = useState(props.report_tabs.all_tabs)
  const [defaultDashboardViews, setDefaultDashboardViews] = useState(props.default_dashboard_views)
  const [customDashboardViews, setCustomDashboardViews] = useState(props.custom_dashboard_views)
  const [customLayouts, setCustomLayouts] = useState(props.custom_layouts)
  const [activeCustomLayout, setActiveCustomLayout] = useState({})
  // set default report period to annual/years options
  const [state, setState] = useState({
    report: props.page || 'analysis',
    entity_type: props.entity_type || 'for-profit',
    pageTitle: capitalize(props.page) || '',
    clientName: props.client_name || '',
    realName: props.real_name || '',
    calendarYearEnd: props.calendarYearEnd,
    fiscalYearStartMonth: props.fiscalYearStartMonth || 1,
    peerviewMetricsYears: props.peerviewMetricsYears || {},
    calcs: props.calcs || {},
    monthly_calcs: props.monthly_calcs || {},
    trailing12: props.trailing12 || {},
    forecastCalcs: {},
    baseCalc: {},
    monthlyForecastCalcs: {},
    fetch: !props.calcs,
    loading: true,
    default_filters: {},
    filters: props?.report_tabs?.active_view?.details || {},
    filterOptions: props.filterOptions || {},
    youAnnualOptions: {},
    youMonthlyOptions: {},
    peerAnnualOptions: {},
    peerMonthlyOptions: {},
    monthlyCompareWithOptions: [],
    tree: props.tree,
    yearRange: props.yearRange || [],
    n: props.n,
    docs_n: 0,
    monthly_avg_n: {},
    monthly_docs_avg_n: {},
    ytd_months: [],
    forecast_scenarios: [],
    insufficient_data: false,
    insufficient_peer_data: false,
    reportSpecific: props.reportSpecific || {},
    isMonthlyAvailable: props.isMonthlyAvailable,
    quarterMonths: ['Q1', [1, 2, 3]],
    trendData: {},
    fiscalYearEnd: props.fiscal_year_end,
    companyYTDDate: null,
    aggregateYTDDate: null,
    companyReportSurvey: {},
    surveyTaken: false,
    surveyResults: {},
    pnlTree: {},
    balTree: {},
  });
  const [filtersModalIsOpen, setFiltersModalIsOpen] = useState(false);
  const [videoModalOpen, setVideoModalOpen] = useState(false);
  const [activeReportView, setActiveReportView] = useState(props.active_report_view);
  const [templateReportView, setTemplateReportView] = useState(props.template_report_view);
  const [reportViewEditIsOpen, setReportViewEditIsOpen] = useState(false);
  const [reportViewForm, setReportViewForm] = useState({
    child_companies_list: props.child_companies_list || [],
    naicsSpecific: 'no',
    parentSpecific: 'no',
    naicsSearch: [],
    parentSearch: [],
    companySearch: [],
    action: 'new',
    userType: 'normal',
    name: '',
    parent_id: null,
    company_id: null,
    admin_default: false,
    admin_custom: false,
    normal_custom: true,
    active_view: false,
    tab_ids: [],
    view_apply: '',
    naics: [],
    parent_ids: [],
    company_ids: []
  })
  const [chatLoading, setChatLoading] = useState(false)
  const [chatLoader, setChatLoader] = useState({
    progress: 0,
    message: 'Initializing analysis process...',
    error: null
  })
  const [jobs, setJobs] = useState({
    id: null, subscribe: false
  })
  const [chat, setChat] = useState(props.chats.length > 0 ? props.chats[0] : {})
  const [allChats, setAllChats] = useState(props.chats)
  const [copilotOpen, setCopilotOpen] = useState(false)
  const [companyNotes, setCompanyNotes] = useState(props.company_notes || [{ id: null, notes: '', preference_id: null }])
  const [activeCompanyNote, setActiveCompanyNote] = useState(props.active_company_note || { id: null, notes: '', preference_id: null })
  const [dashboard, setDashboard] = useState({ id: null, selectedItems: [], layout: [], noChangesMade: true });
  const [editDashboard, setEditDashboard] = useState({
    money: [{ sectionName: 'Revenue & Opex Analysis (Monthly)', sheet: 'rev_and_opex_charts', showDropdown: false }, { sectionName: 'Money Peerview Metrics', sheet: 'peerview_charts', showDropdown: false }, { sectionName: 'Profit & Loss', sheet: 'pnl', showDropdown: false }, { sectionName: 'Balance Sheet', sheet: 'balance_sheet', showDropdown: false }, { sectionName: 'Cash Flow', sheet: 'cash_flow', showDropdown: false }, { sectionName: `KFI's`, sheet: 'kfis', showDropdown: false }],
    people: [{ sectionName: 'People Peerview Metrics', sheet: 'people_peerview_charts', showDropdown: false }, { sectionName: 'People Financial Detail', sheet: 'people_financial_detail', showDropdown: false }, { sectionName: `People KPI`, sheet: 'people_kpi', showDropdown: false }],
    customers: [{ sectionName: 'Customers Peerview Metrics', sheet: 'customers_peerview_charts', showDropdown: false }, { sectionName: 'Customers Financial Detail', sheet: 'customers_financial_detail', showDropdown: false }, { sectionName: `Customers KPI`, sheet: 'customers_kpi', showDropdown: false }]
  })
  const [surveyModalIsOpen, setSurveyModalOpen] = useState(false);
  const [reportTabsPopoverOpen, setReportTabsPopoverOpen] = useState(false);
  const [tabNameModelIsOpen, setTabNameModalOpen] = useState(false);
  const [tabNameForm, setTabNameForm] = useState({
    key: '',
    report: '',
    reportType: '',
    name: '',
    error: null
  });
  const [companyForm, setCompanyForm] = useState({
    id: props.id,
  })
  const [reportBuilderMetrics, setReportBuilderMetrics] = useState({
    id: null,
    allKeys: [],
    editMetrics: false,
    layout: [],
    rev_and_opex_charts: [],
    peerview_charts: [],
    pnl: [],
    balance_sheet: [],
    cash_flow: [],
    kfis: [],
    people_peerview_charts: [],
    people_financial_detail: [],
    people_kpi: [],
    customers_peerview_charts: [],
    customers_financial_detail: [],
    customers_kpi: [],
    financial_detail: [],
    kpi: [],
    allSelectedMetrics: [],
    allSheetMetrics: [],
    noChangesMade: true,
  });
  const [reportBuilderModalState, setReportBuilderModalState] = useState({
    calledAlready: false,
    child_companies_list: props.child_companies_list || [],
    isOpen: false,
    action: 'close',
    edit_view: props.current_view || {},
    current_view: props.current_view || {},
    user_views: props.normal_custom_dashboards || [],
    naicsSpecific: 'no',
    parentSpecific: 'no',
    naicsSearch: [],
    parentSearch: [],
    companySearch: []
  });
  const [reportMetrics, setReportMetrics] = useState({
    id: null,
    allKeys: [],
    editMetrics: false,
    layout: [],
    rev_and_opex_charts: [],
    peerview_charts: [],
    pnl: [],
    balance_sheet: [],
    cash_flow: [],
    kfis: [],
    people_peerview_charts: [],
    people_financial_detail: [],
    people_kpi: [],
    customers_peerview_charts: [],
    customers_financial_detail: [],
    customers_kpi: [],
    financial_detail: [],
    kpi: [],
    allSelectedMetrics: [],
    allSheetMetrics: [],
    noChangesMade: true,
  });
  const [dashboardViewModalState, setDashboardViewModalState] = useState({
    isOpen: false,
    action: 'close',
    edit_view: {},
    current_view: {},
    user_views: [],
    firms_views: [],
    loaded: false
  });
  const [surveyData, setSurveyData] = useState({
    sections: [],
    surveyLayoutData: {},
    surveyCharts: [],
  })
  const [moneyNotes, setMoneyNotes] = useState({
    userNotes: 'Edit Me! Add your own notes here...'
  })
  const [editChartModel, setEditChartModel] = useState({
    isOpen: false,
    chartDetails: null,
    childItem: false,
    highChartOptions: null,
    oldStyles: { seriesOne: { type: 'column' }, seriesTwo: { type: 'column' } },
    newStyles: { seriesOne: { type: 'column' }, seriesTwo: { type: 'column' } }
  })

  const moneyTreesBuilder = (selectedItems, tree, displayByView = 'yearsView') => {
    let moneyKeys = [];
    let pnlOnePageRows = [];
    let balOnePageRows = [];
    let cashFlowRows = [];
    let kfisRows = [];
    const pnlTree = new AccountTree({
      tree: tree['pnl'],
      page: 'money',
      sheet: 'pnl',
      displayByView: displayByView,
      selectedMetrics: selectedItems['pnl']
    })
    pnlTree.forEach((row) => {
      pnlOnePageRows.push(row)
      if (!moneyKeys.includes(row.key)) {
        moneyKeys.push(row.key)
      }
    })
    pnlOnePageRows = pnlOnePageRows.filter(Boolean)

    const balTree = new AccountTree({
      tree: tree['balance_sheet'],
      page: 'money',
      sheet: 'balance_sheet',
      displayByView: displayByView,
      selectedMetrics: selectedItems['balance_sheet']
    })
    balTree.forEach((row) => {
      balOnePageRows.push(row)
      if (!moneyKeys.includes(row.key)) {
        moneyKeys.push(row.key)
      }
    })
    balOnePageRows = balOnePageRows.filter(Boolean)

    const cashFlowTree = new AccountTree({
      tree: tree['cash_flow'],
      page: 'money',
      sheet: 'cash_flow',
      displayByView: displayByView,
      selectedMetrics: selectedItems['cash_flow']
    })
    cashFlowTree.forEach((row) => {
      cashFlowRows.push(row)
      if (!moneyKeys.includes(row.key)) {
        moneyKeys.push(row.key)
      }
    })
    cashFlowRows = cashFlowRows.filter(Boolean)

    const kfisTree = new AccountTree({
      tree: tree['kfis'],
      page: 'money',
      sheet: 'kfis',
      displayByView: displayByView,
      selectedMetrics: selectedItems['kfis']
    })
    kfisTree.forEach((row) => {
      kfisRows.push(row)
      if (!moneyKeys.includes(row.key)) {
        moneyKeys.push(row.key)
      }
    })
    kfisRows = kfisRows.filter(Boolean)
    return { moneyKeys, pnlOnePageRows, balOnePageRows, cashFlowRows, kfisRows }
  }

  const auditTreesBuilder = (tree, displayByView = 'yearsView') => {
    let pnlOnePageRows = [];
    let balOnePageRows = [];
    let kfisRows = [];
    const pnlTree = new AccountTree({
      tree: tree['pnl'],
      page: 'money',
      sheet: 'pnl',
      displayByView: displayByView
    })
    pnlTree.forEach((row) => {
      pnlOnePageRows.push(row)
    })
    pnlOnePageRows = pnlOnePageRows.filter(Boolean)

    const balTree = new AccountTree({
      tree: tree['balance_sheet'],
      page: 'money',
      sheet: 'balance_sheet',
      displayByView: displayByView
    })
    balTree.forEach((row) => {
      balOnePageRows.push(row)
    })
    balOnePageRows = balOnePageRows.filter(Boolean)

    const kfisTree = new AccountTree({
      tree: tree['kfis'],
      page: 'money',
      sheet: 'kfis',
      displayByView: displayByView
    })
    kfisTree.forEach((row) => {
      kfisRows.push(row)
    })
    kfisRows = kfisRows.filter(Boolean)

    return { pnlOnePageRows, balOnePageRows, kfisRows }
  }

  const peopleTreesBuilder = (selectedItems, tree, displayByView = 'yearsView') => {
    let peopleKeys = [];
    let peopleFinancialDetailRows = [];
    let peopleKpiRows = [];

    const pnlTree = new AccountTree({
      tree: tree['pnl'],
      page: 'people',
      sheet: 'pnl',
      displayByView: displayByView,
      selectedMetrics: selectedItems['people_financial_detail']
    })
    pnlTree.forEach((row) => {
      peopleFinancialDetailRows.push(row)
      if (!peopleKeys.includes(row.key)) {
        peopleKeys.push(row.key)
      }
    })
    peopleFinancialDetailRows = peopleFinancialDetailRows.filter(Boolean)

    const kpiTree = new AccountTree({
      tree: tree['kpi_people'],
      page: 'people',
      sheet: 'kpi_people',
      displayByView: displayByView,
      selectedMetrics: selectedItems['people_kpi']
    })
    kpiTree.forEach((row) => {
      peopleKpiRows.push(row)
      if (!peopleKeys.includes(row.key)) {
        peopleKeys.push(row.key)
      }
    })
    peopleKpiRows = peopleKpiRows.filter(Boolean)

    return { peopleKeys, peopleFinancialDetailRows, peopleKpiRows }
  }

  const updateCustomLayout = async (customKey) => {
    let activeCustomLayout = customLayouts.find(c => c.key === customKey)
    try {
      const res = await fetch(`/dashboard_views/update_custom_layout`, {
        method: 'POST',
        headers: {
          'X-CSRF-Token': window.token,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          custom_layout: {
            id: activeCustomLayout.id,
            dashboard_layout: activeCustomLayout.dashboard_layout
          }
        }),
      })

      if (!res.ok) {
        throw new Error('Network response was not ok.');
      } else {
        setCustomLayouts((prevState) => {
          return prevState.map((l) => {
            if (l.key === customKey) {
              l['noChangesMade'] = true
            }
            return l
          })
        })
        toast.success('Changes saved!', {
          position: 'top-right',
          autoClose: 2000,
          hideProgressBar: true,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: false,
          progress: undefined,
        });
      }
    } catch (error) {
      console.log(error)
      toast.error(`Sorry something went wrong. ${error}.`, {
        position: 'top-right',
        autoClose: 5000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: false,
        progress: undefined,
      });
    }
  }

  const handleEditDashBoard = (sheet, report = null) => {
    let finalEditSections = null
    setEditDashboard((prevState) => {
      if (report) {
        finalEditSections = _.cloneDeep(prevState)
        finalEditSections[report] = finalEditSections[report].map(section => {
          if (section['sheet'] === sheet) {
            section['showDropdown'] = !section['showDropdown']
          }
          return section;
        })
      } else {
        let newEditSections = _.cloneDeep(prevState)
        finalEditSections = newEditSections.map(section => {
          if (section['sheet'] === sheet) {
            section['showDropdown'] = !section['showDropdown']
          }
          return section;
        })
      }

      return finalEditSections;
    })
  }

  const countPlainTextCharacters = (html) => {
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;
    const plainText = tempDiv.textContent || tempDiv.innerText || '';
    return plainText.length;
  }

  const onChangeNotes = useCallback(
    (note) => debounce((value) => {
      const cleanHTML = DOMPurify.sanitize(value, sanitizeOptions);
      let characterCount = countPlainTextCharacters(cleanHTML);
      if (characterCount <= 50000) {
        if (note?.id) {
          updateCompanyNotes(cleanHTML, note)
        } else {
          // Happens on page load?
          createCompanyNotes(cleanHTML)
        }
      }
    }, 2000),
    []
  );

  const createCompanyNotes = async (cleanHTML, tab = null) => {
    if (tab?.report === 'company_notes' || activeTab?.report === 'company_notes') {
      setState((prevState) => {
        return {
          ...prevState,
          loading: true
        }
      })
      const newActiveTab = tab ? tab : activeTab
      try {
        const res = await fetch(`/api/v1/company_notes`, {
          method: 'POST',
          headers: {
            'X-CSRF-Token': window.token,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            company_note: {
              notes: cleanHTML,
              preference_id: newActiveTab.id
            }
          }),
        })
  
        const resJson = await res.json()
  
        if (!res.ok) {
          throw new Error('Network response was not ok. Could not save note changes.');
        } else {
          const notes = resJson.message === 'exists' ? resJson.notes : cleanHTML
          const newNote = { notes: notes, id: resJson.id, preference_id: newActiveTab.id }
          setActiveCompanyNote(newNote)
          setCompanyNotes(prevState => {
            return [...prevState, newNote]
          })
          setState((prevState) => {
            return {
              ...prevState,
              loading: false
            }
          })
        }
      } catch (e) {
        console.log(e)
      }
    }
  }

  const updateCompanyNotes = async (cleanHTML, note) => {
    try {
      if (note.notes !== cleanHTML) {
        const res = await fetch(`/api/v1/company_notes/${note.id}`, {
          method: 'PATCH',
          headers: {
            'X-CSRF-Token': window.token,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            company_note: {
              notes: cleanHTML
            }
          }),
        })
        setActiveCompanyNote(prevState => {
          return { ...prevState, notes: cleanHTML }
        })
  
        setCompanyNotes(prevState => { 
          let newNotes = prevState.map(n => {
            return n.preference_id === note.preference_id ? { ...n, notes: cleanHTML } : n
          })
          return newNotes
        })
  
        if (!res.ok) {
          throw new Error('Network response was not ok. Could not save note changes.');
        }
      }
    } catch (e) {
      console.log(e)
    }
  }

  const handleMoneyNotes = (newNotes) => {
    setMoneyNotes(() => {
      return {
        userNotes: newNotes
      }
    })
    setReportMetrics(prevState => {
      let currentView = dashboardViewModalState.current_view
      let noChangesVal = currentView.permission_details === 'view_only' && !dashboardViewModalState.user_views.find(v => v.id === currentView.id)
      return {
        ...prevState,
        noChangesMade: noChangesVal
      }
    })
  }

  const editingParentAndChildLayouts = (report, metricKey, childLayoutKey, selectedMetrics, oldDashboardLayout, oldAllSheetMetrics) => {
    let newLayout = []
    let newSheetRows = null;
    // Toggling a child key in a child/nested layout
    if (childLayoutKey) {
      let childLayout, childMetric = null;
      // Look for parent of child key in previous selected metrics
      let parentMetricIndex = selectedMetrics.findIndex(checked => checked['relatedCheckedKey'] === metricKey);
      if (selectedMetrics[parentMetricIndex]) {
        if (!selectedMetrics[parentMetricIndex].layout) {
          selectedMetrics.splice(parentMetricIndex, 1)
        }
      }
      // update the rows and selection pop up for checked metrics
      // add or remove any metrics for the new check metrics to build the dashboard layout
      newSheetRows = oldAllSheetMetrics.map(m => {
        // Look for parent of child key in previous selected metrics
        let parentMetricIndex = selectedMetrics.findIndex(checked => checked['relatedCheckedKey'] === metricKey);
        if (m['relatedCheckedKey'] === metricKey && m['displayChart']) {
          let childCheckedItemsNumber = 0;
          let childItem = null;

          if (m.layout.length > 0) {
            m.layout = m.layout.map(i => {
              if (i.key.includes('total_revenue_growth_rate_')) {
                i.key = i.key.replace('total_revenue_growth_rate_', 'total_revenue_')
              }
              if (i.key === 'total_revenue_bar_chart') {
                i.key = 'total_revenue_growth_rate'
              }
              i.checked = i.key === childLayoutKey ? !i.checked : i.checked;

              if (i.checked) {
                childCheckedItemsNumber += 1
              }
              return i;
            })
          }

          if (childCheckedItemsNumber) {
            m['checked'] = true
            // if parent doesn't exist in selectedMetrics
            if (parentMetricIndex < 0) {
              let newMetric = _.cloneDeep(m)
              newMetric.layout = newMetric.layout.filter(item => item.checked)
              // 1 child doesn't need child layout
              if (newMetric.layout.length === 1) {
                let soloChild = newMetric.layout[0]
                soloChild.metricRow = m
                if (!Object.hasOwnProperty('relatedCheckedKey')) { soloChild['relatedCheckedKey'] = soloChild.metricRow['relatedCheckedKey'] }
                selectedMetrics.push(soloChild)
              } else {
                selectedMetrics.push(newMetric)
              }
            } else {
              // if parent exists
              let newMetric = _.cloneDeep(m)
              let newChildLayout = newMetric.layout.filter(item => item.checked)
              // 1 child doesn't need child layout
              if (newChildLayout.length === 1) {
                // Check if metric already selected from the Money, People, or Customers. Stay with metric from Money P&L if it already is selected.
                let metricAlreadySelected = selectedMetrics.find(s => s.key === newChildLayout[0].key)
                if (!metricAlreadySelected || (metricAlreadySelected && newChildLayout[0]['metricRow'] && newChildLayout[0]['metricRow']['sheet'] === 'pnl')) {
                  selectedMetrics.splice(parentMetricIndex, 1)
                  let soloChild = newChildLayout[0]
                  soloChild.metricRow = m
                  if (!Object.hasOwnProperty('relatedCheckedKey')) { soloChild['relatedCheckedKey'] = soloChild.metricRow['relatedCheckedKey'] }
                  selectedMetrics.push(soloChild)
                }
              } else {
                // handle parent metrics with child layouts
                if (selectedMetrics[parentMetricIndex].layout) {
                  selectedMetrics[parentMetricIndex].layout = newChildLayout
                } else {
                  // More than 1 child will need a parent
                  selectedMetrics.splice(parentMetricIndex, 1)
                  newMetric.layout = newChildLayout;
                  selectedMetrics.push(newMetric)
                }
              }
            }
          } else {
            // if no child keys are checked remove the parent metric if exists
            m['checked'] = false
            if (parentMetricIndex > -1) { selectedMetrics.splice(parentMetricIndex, 1) }
          }
        }
        return m;
      })
    } else {
      // Deals with metrics not in child layouts
      let oneAlreadyFound = 0
      let allChecked = null;

      newSheetRows = oldAllSheetMetrics.map(m => {
        let checkedMetricIndex = selectedMetrics.findIndex(checked => checked['relatedCheckedKey'] === metricKey)

        if ((m['relatedCheckedKey'] === metricKey && m['displayChart']) || (report === 'customers' && m['relatedCheckedKey'] === metricKey && m['displayChart'])) {
          m['checked'] = oneAlreadyFound ? allChecked : !m['checked']
          oneAlreadyFound += 1
          if (oneAlreadyFound === 1) { allChecked = m['checked'] }

          if (m.layout && m.layout.length > 0) {
            m.layout = m.layout.map(i => {
              if (!i.key.includes('_notes') && m['checked']) {
                i.checked = m['checked'];
              } else if (!m['checked']) {
                i.checked = false
              }
              return i;
            })
          }

          if (m['checked']) {
            if (checkedMetricIndex < 0) {
              m.onManyReports = oneAlreadyFound > 1
              selectedMetrics.push(m)
            }
          } else {
            if (checkedMetricIndex > -1) {
              selectedMetrics.splice(checkedMetricIndex, 1)
            }
          }
        }
        return m
      })
    }

    selectedMetrics.forEach((s, i) => {
      let oldParentItem = null;
      let parentLayout = [];
      oldParentItem = oldDashboardLayout.find(o => o['i'] === s.key);
      if (oldParentItem && s.layout) {
        let parentH = 2;
        let parentW = 3;
        let widthCounter = 0
        s.layout.forEach((c) => {
          let oldChildItem = oldParentItem.layout.find(l => l.i === c.key)
          if (oldChildItem && c['checked']) {
            parentLayout.push(oldChildItem)
            if (widthCounter >= 3) {
              parentH += oldChildItem.h;
              widthCounter = 0
            }
            parentW += oldChildItem.w;
            widthCounter += oldChildItem.w;
          } else if (c['checked']) {
            let newChildItem = layoutMaker(c, i)
            parentLayout.push(newChildItem)
            if (widthCounter >= 3) {
              parentH += newChildItem.h;
              widthCounter = 0
            }
            parentW += newChildItem.w;
            widthCounter += newChildItem.w;
          }
        })

        oldParentItem.layout = parentLayout;
        oldParentItem.w = parentW > 3 ? 3 : parentW
        oldParentItem.h = parentH
        newLayout.push(oldParentItem)
      } else if (oldParentItem) {
        newLayout.push(oldParentItem)
      } else {
        // parent with kids
        if (s['layout']) {
          let parentH = 2;
          let parentW = 3;
          let widthCounter = 0
          let miniLayout = s['layout'].filter(child => child['checked']).map((l, i) => {
            let layoutItem = layoutMaker(l, i)
            if (widthCounter >= 3) {
              parentH += layoutItem.h;
              widthCounter = 0
            }
            parentW += layoutItem.w;
            widthCounter += layoutItem.w;
            return layoutItem;
          })
          newLayout.push({
            i: s.key,
            isBounded: false,
            isDraggable: true,
            isResizable: true,
            x: ((i * 3) % (6)),
            y: Infinity,
            w: parentW > 3 ? 3 : parentW,
            h: parentH,
            minW: 1,
            minH: 1,
            maxW: 6,
            maxH: 12,
            resizeHandles: ['se'],
            layout: miniLayout,
            metricRow: s
          })
        } else {
          // no kids
          let layoutItem = layoutMaker(s, metricKey)
          newLayout.push(layoutItem);
        }
      }
    })

    return { newRows: newSheetRows, newLayout: newLayout };
  }

  const handleReportBuilderSelectAndLayout = (page, sheet, metricKey, childLayoutKey = '', report = null) => {
    let newLayout = []
    let selectedMetrics = null;

    setReportBuilderMetrics((prevState) => {
      selectedMetrics = _.cloneDeep(prevState.allSelectedMetrics);
      let newSheetRows = null;
      let oldDashboardLayout = _.cloneDeep(prevState.layout)
      let oldAllSheetMetrics = _.cloneDeep(prevState.allSheetMetrics)
      let actualPage = page === 'money' ? report : page
      let { newRows, newLayout } = editingParentAndChildLayouts(actualPage, metricKey, childLayoutKey, selectedMetrics, oldDashboardLayout, oldAllSheetMetrics)

      newLayout = newLayout.filter(l => {
        if (l.metricRow) {
          return reportMetrics.allKeys.includes(l.i) || reportMetrics.allKeys.includes(l.metricRow?.key)
        } else {
          return true
        }
      })

      return {
        ...prevState,
        rev_and_opex_charts: newRows.filter(m => m.sheet === 'rev_and_opex_charts'),
        peerview_charts: newRows.filter(m => m.sheet === 'peerview_charts'),
        pnl: newRows.filter(m => m.sheet === 'pnl'),
        balance_sheet: newRows.filter(m => m.sheet === 'balance_sheet'),
        cash_flow: newRows.filter(m => m.sheet === 'cash_flow'),
        kfis: newRows.filter(m => m.sheet === 'kfis'),
        people_peerview_charts: newRows.filter(m => m.sheet === 'people_peerview_charts'),
        people_financial_detail: newRows.filter(m => m.sheet === 'people_financial_detail'),
        people_kpi: newRows.filter(m => m.sheet === 'people_kpi'),
        customers_peerview_charts: newRows.filter(m => m.sheet === 'customers_peerview_charts'),
        customers_financial_detail: newRows.filter(m => m.sheet === 'customers_financial_detail'),
        customers_kpi: newRows.filter(m => m.sheet === 'customers_kpi'),
        allSelectedMetrics: selectedMetrics,
        allSheetMetrics: newRows,
        layout: newLayout,
        noChangesMade: false
      }
    })
  }

  const handleSelectAndLayout = (page, sheet, metricKey, childLayoutKey = '', report = null) => {
    let newLayout = []
    let selectedMetrics = null;

    setReportMetrics((prevState) => {
      selectedMetrics = _.cloneDeep(prevState.allSelectedMetrics);
      let newSheetRows = null;
      let oldDashboardLayout = _.cloneDeep(prevState.layout)
      let oldAllSheetMetrics = _.cloneDeep(prevState.allSheetMetrics)
      let actualPage = page === 'money' ? report : page
      let { newRows, newLayout } = editingParentAndChildLayouts(actualPage, metricKey, childLayoutKey, selectedMetrics, oldDashboardLayout, oldAllSheetMetrics)

      newLayout = newLayout.filter(l => {
        if (l.metricRow) {
          return reportMetrics.allKeys.includes(l.i) || reportMetrics.allKeys.includes(l.metricRow?.key)
        } else {
          return true
        }
      })

      return {
        ...prevState,
        rev_and_opex_charts: newRows.filter(m => m.sheet === 'rev_and_opex_charts'),
        peerview_charts: newRows.filter(m => m.sheet === 'peerview_charts'),
        pnl: newRows.filter(m => m.sheet === 'pnl'),
        balance_sheet: newRows.filter(m => m.sheet === 'balance_sheet'),
        cash_flow: newRows.filter(m => m.sheet === 'cash_flow'),
        kfis: newRows.filter(m => m.sheet === 'kfis'),
        people_peerview_charts: newRows.filter(m => m.sheet === 'people_peerview_charts'),
        people_financial_detail: newRows.filter(m => m.sheet === 'people_financial_detail'),
        people_kpi: newRows.filter(m => m.sheet === 'people_kpi'),
        customers_peerview_charts: newRows.filter(m => m.sheet === 'customers_peerview_charts'),
        customers_financial_detail: newRows.filter(m => m.sheet === 'customers_financial_detail'),
        customers_kpi: newRows.filter(m => m.sheet === 'customers_kpi'),
        allSelectedMetrics: selectedMetrics,
        allSheetMetrics: newRows,
        layout: newLayout,
        noChangesMade: false
      }
    })
  }

  const handleDashboardSelections = (item, parentItemInfo = null) => {
    let itemsCheckedTrue = [];
    setDashboard((prevState) => {
      let oldDashInfo = _.cloneDeep(prevState);
      if (parentItemInfo) {
        let parentItem = oldDashInfo.selectedItems.find((c) => c.key === parentItemInfo.key);
        if (parentItem.children) {
          let childItem = parentItem.children.find((child) => child.key === item.key);
          childItem.checked = !childItem.checked
          let numberOfChildrenChecked = parentItem.children.filter(child => child.checked)
          parentItem.checked = numberOfChildrenChecked.length > 0
        }
      } else {
        let selectedChart = oldDashInfo.selectedItems.find((c) => c.key === item.key);
        let checkStatus = !selectedChart.checked
        if (selectedChart.children) {
          selectedChart.children = selectedChart.children.map((c) => {
            c.checked = checkStatus
            return c;
          })
        }
        selectedChart.checked = checkStatus;
      }

      oldDashInfo.selectedItems.forEach((c) => {
        if (c.checked) {
          itemsCheckedTrue.push(c.key)
        }
      })

      let chartsLength = itemsCheckedTrue.length;
      let newLayout = itemsCheckedTrue.map((c, i) => {
        let itemLayout = oldDashInfo.layout.find((d) => d['i'] === c);
        if (itemLayout) {
          return itemLayout
        } else {
          if (c.includes('_gauges')) {
            return { i: c, isBounded: false, isDraggable: true, isResizable: false, x: 0, y: 0, w: 6, h: 1, minW: 6, minH: 1, maxW: 6, maxH: 12, resizeHandles: ['se'] }
          } else if (c.includes('pie')) {
            return {
              i: c,
              isBounded: false,
              isDraggable: true,
              isResizable: true,
              x: ((i * 2) % (6)),
              y: ((i * 2) % (3)),
              w: 2,
              h: 2,
              minW: 1,
              minH: 1,
              maxW: 6,
              maxH: 12,
              resizeHandles: ['se']
            }
          } else {
            return {
              i: c,
              isBounded: false,
              isDraggable: true,
              isResizable: true,
              x: ((i * 2) % (6)),
              y: Infinity,
              w: 2,
              h: 1,
              minW: 1,
              minH: 1,
              maxW: 6,
              maxH: 12,
              resizeHandles: ['se']
            }
          }
        }
      })

      return { ...prevState, selectedItems: oldDashInfo.selectedItems, layout: newLayout, noChangesMade: false }
    })
  }

  const handleSelectAll = (select) => {
    setDashboard((prevState) => {
      let newLayout = []
      let oldDashInfo = _.cloneDeep(prevState);
      let allItems = oldDashInfo.selectedItems.map((item) => {
        if (item.children) {
          item.children = item.children.map((c) => {
            c.checked = select
            return c;
          })
        }
        item.checked = select;
        return item;
      })

      allItems.forEach((c, i) => {
        if (c.checked) {
          let itemLayout = oldDashInfo.layout.find((d) => d['i'] === c.key);
          if (itemLayout) {
            newLayout.push(itemLayout)
          } else {
            if (c.key.includes('_gauges')) {
              newLayout.push({ i: c.key, isBounded: false, isDraggable: true, isResizable: false, x: 0, y: 0, w: 6, h: 1, minW: 6, minH: 1, maxW: 6, maxH: 12, resizeHandles: ['se'] })
            } else if (c.key.includes('pie')) {
              newLayout.push({
                i: c.key,
                isBounded: false,
                isDraggable: true,
                isResizable: true,
                x: ((i * 2) % (6)),
                y: ((i * 2) % (3)),
                w: 2,
                h: 2,
                minW: 1,
                minH: 1,
                maxW: 6,
                maxH: 12,
                resizeHandles: ['se']
              })
            } else {
              newLayout.push({
                i: c.key,
                isBounded: false,
                isDraggable: true,
                isResizable: true,
                x: ((i * 2) % (6)),
                y: Infinity,
                w: 2,
                h: 1,
                minW: 1,
                minH: 1,
                maxW: 6,
                maxH: 12,
                resizeHandles: ['se']
              })
            }
          }
        }
      })

      return { ...prevState, selectedItems: allItems, layout: newLayout, noChangesMade: false }
    })
  }

  const createCustomReport = async (newDashView) => {
    if (newDashView.viewApply === 'all_firm_clients_naics') {
      let company = reportBuilderModalState.child_companies_list.find(c => c[0] === companyForm.id)
      newDashView.naics = [company[3]]
    }

    let newView = {
      company_id: props.admin_editor ? null : companyForm.id,
      editor_page: true,
      name: newDashView.name,
      report_type: 'money',
      private: newDashView.viewVisibilty === 'only_me',
      permission_details: newDashView.viewPermission,
      view_apply: newDashView.viewApply,
      admin_custom_default: props.admin_editor,
      normal_custom: !props.admin_editor,
      naics: newDashView.naics,
      parent_ids: newDashView.parent_ids,
      company_ids: newDashView.company_ids
    }

    if (report === 'money' || report === 'analysis') {
      newView['selected_items'] = {
        rev_and_opex_charts: reportBuilderMetrics['rev_and_opex_charts'],
        peerview_charts: reportBuilderMetrics['peerview_charts'],
        pnl: reportBuilderMetrics['pnl'],
        balance_sheet: reportBuilderMetrics['balance_sheet'],
        cash_flow: reportBuilderMetrics['cash_flow'],
        kfis: reportBuilderMetrics['kfis'],
        money_all_selected_items: reportBuilderMetrics['allSelectedMetrics'],
        money_user_notes: '',
        people_peerview_charts: reportBuilderMetrics['people_peerview_charts'],
        people_financial_detail: reportBuilderMetrics['people_financial_detail'],
        people_kpi: reportBuilderMetrics['people_kpi'],
        customers_peerview_charts: reportBuilderMetrics['customers_peerview_charts'],
        customers_financial_detail: reportBuilderMetrics['customers_financial_detail'],
        customers_kpi: reportBuilderMetrics['customers_kpi'],
      }
      newView['dashboard_layout'] = reportBuilderMetrics['layout']
    }

    try {
      const res = await fetch(`/dashboard_views`, {
        method: 'POST',
        headers: {
          'X-CSRF-Token': window.token,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(newView),
      })
      const dashViewRes = await res.json();

      if (!res.ok) {
        throw new Error('Network response was not ok.');
      } else {
        if (report === 'money' || report === 'analysis') {
          setReportBuilderMetrics(prevState => {
            return {
              ...prevState,
              id: dashViewRes.current_view.id,
              noChangesMade: true
            }
          })

          setReportBuilderModalState(prevState => {
            return {
              ...prevState,
              current_view: dashViewRes.current_view,
              user_views: dashViewRes.user_views,
              isOpen: false
            }
          })

          setCustomDashboardViews(prevState => {
            return {
              ...prevState,
              by_user: [...prevState.by_user, dashViewRes.current_view]
            }
          })
        }
        toast.success('New Report created!', {
          position: 'top-right',
          autoClose: 2000,
          hideProgressBar: true,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: false,
          progress: undefined,
        });
      }
    } catch (error) {
      console.log(error);
      toast.error(`Sorry something went wrong. ${error}.`, {
        position: 'top-right',
        autoClose: 5000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: false,
        progress: undefined,
      });
    }
  }

  const deleteCustomReport = async (e, dashViewId) => {
    e.preventDefault()
    let dashboard = reportBuilderModalState.user_views.find(v => v.id === dashViewId)
    let noViewEdit = dashboard.permission_details === 'view_only' && props.user_id !== dashboard.user_id

    if (noViewEdit) {
      toast.error(`Sorry You Are NOT Allowed to Edit this Dashboard View. You need to be the owner of this dashboard view in order to edit.`, {
        position: 'top-right',
        autoClose: 10000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: false,
        progress: undefined,
      });
    } else {
      try {
        const res = await fetch(`/dashboard_views/${dashViewId}`, {
          method: 'DELETE',
          headers: {
            'X-CSRF-Token': window.token,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ editor_page: true }),
        })

        if (!res.ok) {
          throw new Error('Network response was not ok.');
        } else {
          let newCurrentView = reportBuilderModalState.user_views.length > 0 ? reportBuilderModalState.user_views[0] : {}
          let currentViewDeleted = dashViewId === reportBuilderModalState.current_view.id
          setReportBuilderModalState(prevState => {
            let oldUserViews = prevState.user_views
            let newUserViews = oldUserViews.filter(v => v.id !== dashViewId)
            if (currentViewDeleted) {
              newCurrentView = newUserViews.length > 0 ? newUserViews[0] : {}
              return {
                ...prevState,
                current_view: newCurrentView,
                user_views: newUserViews,
              }
            } else {
              return {
                ...prevState,
                user_views: newUserViews,
              }
            }
          })

          if ((report === 'money' || report === 'analysis') && currentViewDeleted && Object.keys(newCurrentView).length > 0) {
            let allKeys = [];
            let layout = [];
            let revOpexCharts = moneyCharts['rev_and_opex_charts'];
            let peerviewCharts = null;
            let peerviewChartsSelected = [];
            let peopleLayout = [];
            let peoplePeerviewCharts = [];
            let peoplePeerviewChartsSelected = [];
            let peopleFDRows = []
            let customersLayout = [];
            let customersPeerviewCharts = [];
            let customersPeerviewChartsSelected = [];
            let customersFDRows = []
            let customersKpiRows = customersKPIsFormat();

            if (newCurrentView?.['selected_items']?.['rev_and_opex_charts']?.length > 0) {
              revOpexCharts = newCurrentView['selected_items']['rev_and_opex_charts']
            }

            if (newCurrentView['selected_items'] && newCurrentView['selected_items']['peerview_charts'].length > 0) {
              let { selectedItems } = mergeItems(report, newCurrentView['selected_items']['peerview_charts'], []);
              peerviewCharts = selectedItems;
              peerviewCharts.forEach((chart) => {
                if (chart.checked) {
                  peerviewChartsSelected.push(chart)
                }
              })
              layout = newCurrentView['dashboard_layout']
            } else {
              peerviewCharts = moneyCharts['peerview_charts']
              peerviewCharts.forEach((chart) => {
                if (chart.checked) {
                  let defaultLayout = defaultLayouts['money_peerview_charts'].find((l) => l.i === chart.key)
                  if (defaultLayout) {
                    layout.push(defaultLayout)
                    peerviewChartsSelected.push(chart)
                  }
                }
              })
            }

            if (newCurrentView['selected_items'] && newCurrentView['selected_items']['people_peerview_charts'] && newCurrentView['selected_items']['people_peerview_charts'].length > 0) {
              let { selectedItems } = mergeItems('people', newCurrentView['selected_items']['people_peerview_charts'], []);
              peoplePeerviewCharts = selectedItems;
            } else {
              peoplePeerviewCharts = peopleCharts['peerview_charts']
            }

            if (newCurrentView['selected_items'] && newCurrentView['selected_items']['customers_peerview_charts'] && newCurrentView['selected_items']['customers_peerview_charts'].length > 0) {
              let { selectedItems } = mergeItems('customers', newCurrentView['selected_items']['customers_peerview_charts'], []);
              customersPeerviewCharts = selectedItems;
            } else {
              customersPeerviewCharts = customerCharts['peerview_charts']
            }

            let displayByView = 'yearsView'
            if (filters['display_columns_by'] === 'Years' && filters['report_period'] === 'Year to Date') {
              displayByView = 'ytdView'
            }
            if (filters['display_columns_by'] === 'Months') {
              displayByView = 'monthsView'
            }

            if (report === 'money' || report === 'analysis') {
              let { moneyKeys, pnlOnePageRows, balOnePageRows, cashFlowRows, kfisRows } = moneyTreesBuilder(newCurrentView['selected_items'], state.tree, displayByView)
              let { peopleKeys, peopleFinancialDetailRows, peopleKpiRows } = peopleTreesBuilder(newCurrentView['selected_items'], state.tree, displayByView)

              // People & Customers contain similar metrics found in Money P&L
              if (!newCurrentView['selected_items']['people_financial_detail']) {
                peopleFDRows = updatePeopleCustomersChecks(peopleFinancialDetailRows, newCurrentView['selected_items']['pnl'])
              } else {
                peopleFDRows = peopleFinancialDetailRows
              }

              if (!newCurrentView['selected_items']['customers_financial_detail']) {
                customersFDRows = updatePeopleCustomersChecks(customersFinancialDetailRows, newCurrentView['selected_items']['pnl'])
              } else {
                customersFDRows = updateCustomersRows(newCurrentView['selected_items']['customers_financial_detail'], customersFinancialDetailRows)
              }

              if (!newCurrentView['selected_items']['customers_kpi']) {
                customersKpiRows = updatePeopleCustomersChecks(customersKpiRows, newCurrentView['selected_items']['pnl'])
              } else {
                customersKpiRows = updateCustomersRows(newCurrentView['selected_items']['customers_kpi'], customersKpiRows)
              }

              let customerFDKeys = customersFDRows.map(cfdr => cfdr.key)
              let customersKpiKeys = customersKpiRows.map(ck => ck.key)

              layout = layout.filter(l => {
                if (l.metricRow) {
                  return reportMetrics.allKeys.includes(l.i) || reportMetrics.allKeys.includes(l.metricRow?.key)
                } else {
                  return true
                }
              })

              let selectedItems = newCurrentView['selected_items']['money_all_selected_items'].length > 0 ? newCurrentView['selected_items']['money_all_selected_items'] : peerviewChartsSelected;
              newCurrentView.selected_items = selectedItems
              newCurrentView.dashboard_layout = layout

              setReportBuilderMetrics(prevState => {
                return {
                  ...prevState,
                  id: newCurrentView.id,
                  layout: layout,
                  rev_and_opex_charts: revOpexCharts,
                  peerview_charts: peerviewCharts,
                  pnl: pnlOnePageRows,
                  balance_sheet: balOnePageRows,
                  cash_flow: cashFlowRows,
                  kfis: kfisRows,
                  people_peerview_charts: peoplePeerviewCharts,
                  people_financial_detail: peopleFDRows,
                  people_kpi: peopleKpiRows,
                  customers_peerview_charts: customersPeerviewCharts,
                  customers_financial_detail: customersFDRows,
                  customers_kpi: customersKpiRows,
                  allSelectedMetrics: selectedItems,
                  allSheetMetrics: [...revOpexCharts, ...peerviewCharts, ...pnlOnePageRows, ...balOnePageRows, ...cashFlowRows, ...kfisRows, ...peoplePeerviewCharts, ...peopleFDRows, ...peopleKpiRows, ...customersPeerviewCharts, ...customersFDRows, ...customersKpiRows],
                  noChangesMade: true
                }
              })

              setReportBuilderModalState((prevState) => {
                return {
                  ...prevState,
                  current_view: newCurrentView
                }
              })
            }
          }

          setCustomDashboardViews(prevState => {
            let newByUser = prevState.by_user.filter(r => r.id !== dashViewId)
            return {
              ...prevState,
              by_user: newByUser
            }
          })

          toast.success('Report was deleted!', {
            position: 'top-right',
            autoClose: 2000,
            hideProgressBar: true,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: false,
            progress: undefined,
          });
        }
      } catch (error) {
        console.log(error);
        toast.error('Sorry something went wrong. Try again later.', {
          position: 'top-right',
          autoClose: 2000,
          hideProgressBar: true,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: false,
          progress: undefined,
        });
      }
    }
  }


  // Create a copy of tabs array and remove the aggregates property.
  const prepareTabsForView = () => {
    return reportsOnTabs.map(({ aggregates, loaded, ...restOfTab }) => {
      let details = restOfTab.details;
      if (!details ||
        typeof details !== "object" ||
        Object.keys(details).length === 0) {
        details = state.default_filters;
      }
      return {
        ...restOfTab,
        details,
      };
    })
  }

  const mergeCompanyNotes = (companyNoteIds) => {
    setCompanyNotes(prevState => {
      const incomingIds = companyNoteIds.map(note => note.id)
      const oldNotes = prevState.filter(note => !incomingIds.includes(note.id))
      return [...oldNotes, ...companyNoteIds]
    })
  }

  const createReportView = async (newReportView) => {
    let changes = confirm("Are you sure you want to create this report view? Users get their own copies of this new view and not affect your changes.");

    if (changes === true) {
      let cleanTabs = prepareTabsForView();
      let newView = {
        company_id: props.id,
        name: reportViewForm.name,
        view_apply: newReportView.viewApply,
        admin_default: reportViewForm.userType === 'admin',
        normal_custom: reportViewForm.userType === 'normal',
        tabs_open: cleanTabs,
        naics: newReportView.naics,
        parent_ids: newReportView.parent_ids,
        company_ids: newReportView.company_ids,
        page: props.page
      }

      try {
        const res = await fetch(`/report_views`, {
          method: 'POST',
          headers: {
            'X-CSRF-Token': window.token,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(newView),
        })
        const reportViewRes = await res.json();

        if (!res.ok) {
          throw new Error('Network response was not ok.');
        } else {
          mergeCompanyNotes(reportViewRes.new_company_notes);
          setTemplateReportView(reportViewRes);
          setActiveReportView(reportViewRes.report_view.report_view);
          let newTabs = reportViewRes.report_view.report_view_tabs

          setActiveTab(prevState => {
            let newActiveTab = newTabs.find(t => t.old_tab_id === prevState.id)
            return { ...prevState, id: newActiveTab.copy_tab.id, original_company_note_id: newActiveTab.copy_tab.original_company_note_id }
          })
          setReportsOnTabs(prevState => {
            return prevState.map(oldTab => {
              let newTab = newTabs.find(t => t.old_tab_id === oldTab.id)
              if (newTab) {
                return { ...oldTab, id: newTab.copy_tab.id, original_company_note_id: newTab.copy_tab.original_company_note_id }
              }
              return oldTab
            })
          })

          setAllReportTabs(prevState => {
            return prevState.map(oldTab => {
              let newTab = newTabs.find(t => t.old_tab_id === oldTab.id)
              if (newTab) {
                return { ...oldTab, id: newTab.copy_tab.id, original_company_note_id: newTab.copy_tab.original_company_note_id }
              }
              return oldTab
            })
          })

          toast.success('Report View Created!', {
            position: 'top-right',
            autoClose: 2000,
            hideProgressBar: true,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: false,
            progress: undefined,
          });
        }
      } catch (error) {
        console.log(error);
        let errorMessage = 'New report view was not created properly'
        toast.error(`Sorry something went wrong. ${errorMessage}.`, {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: true,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: false,
          progress: undefined,
        });
      }
    }
  }

  const updateReportView = async (updatedReportView) => {
    let changes = confirm("Are you sure you want to update this report view? This won't affect any user-created copies.");

    if (changes === true) {
      let cleanTabs = prepareTabsForView();
      let updatedView = {
        company_id: props.id,
        name: reportViewForm.name,
        view_apply: updatedReportView.viewApply,
        admin_default: reportViewForm.userType === 'admin',
        normal_custom: reportViewForm.userType === 'normal',
        tabs_open: cleanTabs,
        naics: updatedReportView.naics,
        parent_ids: updatedReportView.parent_ids,
        company_ids: updatedReportView.company_ids,
        page: props.page
      }

      try {
        let templateInfo = templateReportView?.template_report_view;
        const res = await fetch(`/report_views/${templateInfo.id}`, {
          method: 'PATCH',
          headers: {
            'X-CSRF-Token': window.token,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(updatedView),
        })
        const reportViewRes = await res.json();

        if (!res.ok) {
          throw new Error('Network response was not ok.');
        } else {
          mergeCompanyNotes(reportViewRes.new_company_notes);
          setTemplateReportView(reportViewRes);
          toast.success('Report View Changes Saved!', {
            position: 'top-right',
            autoClose: 2000,
            hideProgressBar: true,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: false,
            progress: undefined,
          });
        }
      } catch (error) {
        console.log(error);
        let errorMessage = 'Report view changes not saved properly.'
        toast.error(`Sorry something went wrong. ${errorMessage}.`, {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: true,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: false,
          progress: undefined,
        });
      }
    }
  }

  const archiveReportView = async () => {
    let changes = confirm("Are you sure you want to archive this report view? This will hide it from others, but won't affect any user-created copies.");
    if (changes === true) {
      let templateInfo = templateReportView?.template_report_view;
      try {
        const res = await fetch(`/report_views/${templateInfo.id}/archive`, {
          method: 'PATCH',
          headers: {
            'X-CSRF-Token': window.token,
            'Content-Type': 'application/json',
          },
        })

        if (!res.ok) {
          throw new Error('Network response was not ok.');
        } else {
          toast.success('Report View was archived!', {
            position: 'top-right',
            autoClose: 2000,
            hideProgressBar: true,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: false,
            progress: undefined,
          });
        }
      } catch (error) {
        console.log(error);
        let errorMessage = 'Report view was not archived properly.'
        toast.error(`Sorry something went wrong. ${errorMessage}.`, {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: true,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: false,
          progress: undefined,
        });
      }
    }
  }

  const createDashBoardView = async (newDashView) => {
    let newView = {
      name: newDashView.name,
      private: newDashView.viewVisibilty === 'only_me',
      report_type: report,
      permission_details: newDashView.viewPermission,
      view_apply: newDashView.viewApply
    }

    if (report === 'money' || report === 'analysis') {
      newView['selected_items'] = {
        rev_and_opex_charts: reportMetrics['rev_and_opex_charts'],
        peerview_charts: reportMetrics['peerview_charts'],
        pnl: reportMetrics['pnl'],
        balance_sheet: reportMetrics['balance_sheet'],
        cash_flow: reportMetrics['cash_flow'],
        kfis: reportMetrics['kfis'],
        money_all_selected_items: reportMetrics['allSelectedMetrics'],
        money_user_notes: moneyNotes['userNotes'],
        people_peerview_charts: reportMetrics['people_peerview_charts'],
        people_financial_detail: reportMetrics['people_financial_detail'],
        people_kpi: reportMetrics['people_kpi'],
        customers_peerview_charts: reportMetrics['customers_peerview_charts'],
        customers_financial_detail: reportMetrics['customers_financial_detail'],
        customers_kpi: reportMetrics['customers_kpi'],
      }
      newView['dashboard_layout'] = reportMetrics['layout']
    } else if (report === 'customers') {
      newView['selected_items'] = {
        peerview_charts: reportMetrics['peerview_charts'],
        pnl: reportMetrics['pnl'],
        balance_sheet: reportMetrics['balance_sheet'],
        cash_flow: reportMetrics['cash_flow'],
        kfis: reportMetrics['kfis'],
        all_selected_items: reportMetrics['allSelectedMetrics']
      }
      newView['dashboard_layout'] = reportMetrics['layout']
    } else {
      newView['selected_items'] = dashboard.selectedItems
      newView['dashboard_layout'] = dashboard.layout
    }

    try {
      const res = await fetch(`/dashboard_views`, {
        method: 'POST',
        headers: {
          'X-CSRF-Token': window.token,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(newView),
      })
      const dashViewRes = await res.json();

      if (!res.ok) {
        throw new Error('Network response was not ok.');
      } else {
        if (report === 'money' || report === 'analysis' || report === 'customers') {
          setReportMetrics(prevState => {
            return {
              ...prevState,
              id: dashViewRes.current_view.id,
              noChangesMade: true
            }
          })

          setDashboardViewModalState(prevState => {
            return {
              ...prevState,
              current_view: dashViewRes.current_view,
              user_views: dashViewRes.user_views,
              firms_views: dashViewRes.firms_views
            }
          })
        } else {
          setDashboard((prevState) => {
            return {
              ...prevState,
              id: dashViewRes.current_view.id,
              noChangesMade: true
            }
          })
          setDashboardViewModalState(prevState => {
            return {
              ...prevState,
              current_view: dashViewRes.current_view,
              user_views: dashViewRes.user_views,
              firms_views: dashViewRes.firms_views
            }
          })
        }
        toast.success('Created!', {
          position: 'top-right',
          autoClose: 2000,
          hideProgressBar: true,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: false,
          progress: undefined,
        });
      }
    } catch (error) {
      console.log(error);
      let errorMessage = 'New view was not created properly'
      toast.error(`Sorry something went wrong. ${errorMessage}.`, {
        position: 'top-right',
        autoClose: 5000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: false,
        progress: undefined,
      });
    }
  }

  const getCustomReport = async (dashViewId) => {
    try {
      const res = await fetch(`/dashboard_views/${dashViewId}?editor_page=true`, {
        method: 'GET',
        headers: {
          'X-CSRF-Token': window.token,
          'Content-Type': 'application/json',
        },
      })
      const dashViewRes = await res.json();

      if (!res.ok) {
        throw new Error('Network response was not ok.');
      } else {
        let currentDash = dashViewRes['current_view']
        if (report === 'money' || report === 'analysis') {
          let allKeys = [];
          let allSelectedKeys = []
          let layout = [];
          let revOpexCharts = moneyCharts['rev_and_opex_charts'];
          let peerviewCharts = null;
          let peerviewChartsSelected = [];
          let peopleLayout = [];
          let peoplePeerviewCharts = [];
          let peoplePeerviewChartsSelected = [];
          let peopleFDRows = []
          let customersLayout = [];
          let customersPeerviewCharts = [];
          let customersPeerviewChartsSelected = [];
          let customersFDRows = []
          let customersKpiRows = customersKPIsFormat();

          if (currentDash?.['selected_items']?.['rev_and_opex_charts']?.length > 0) {
            revOpexCharts = currentDash['selected_items']['rev_and_opex_charts']
          }

          if (currentDash['selected_items']['peerview_charts'].length) {
            let { selectedItems } = mergeItems(report, currentDash['selected_items']['peerview_charts'], []);
            peerviewCharts = selectedItems;
            peerviewCharts.forEach((chart) => {
              if (chart.checked) {
                peerviewChartsSelected.push(chart)
              }
            })

            layout = currentDash['dashboard_layout']
          } else {
            peerviewCharts = moneyCharts['peerview_charts']
            peerviewCharts.forEach((chart) => {
              if (chart.checked) {
                let defaultLayout = defaultLayouts['money_peerview_charts'].find((l) => l.i === chart.key)
                if (defaultLayout) {
                  layout.push(defaultLayout)
                  peerviewChartsSelected.push(chart)
                }
              }
            })
          }

          if (currentDash['selected_items'] && currentDash['selected_items']['people_peerview_charts'] && currentDash['selected_items']['people_peerview_charts'].length > 0) {
            let { selectedItems } = mergeItems('people', currentDash['selected_items']['people_peerview_charts'], []);
            peoplePeerviewCharts = selectedItems;
          } else {
            peoplePeerviewCharts = peopleCharts['peerview_charts']
          }

          if (currentDash['selected_items'] && currentDash['selected_items']['customers_peerview_charts'] && currentDash['selected_items']['customers_peerview_charts'].length > 0) {
            let { selectedItems } = mergeItems('customers', currentDash['selected_items']['customers_peerview_charts'], []);
            customersPeerviewCharts = selectedItems;
          } else {
            customersPeerviewCharts = customerCharts['peerview_charts']
          }

          let displayByView = 'yearsView'
          if (filters['display_columns_by'] === 'Years' && filters['report_period'] === 'Year to Date') {
            displayByView = 'ytdView'
          }
          if (filters['display_columns_by'] === 'Months') {
            displayByView = 'monthsView'
          }
          // Money is going to have all people and customers
          if (report === 'money' || report === 'analysis') {
            let { moneyKeys, pnlOnePageRows, balOnePageRows, cashFlowRows, kfisRows } = moneyTreesBuilder(currentDash['selected_items'], state.tree, displayByView)
            let { peopleKeys, peopleFinancialDetailRows, peopleKpiRows } = peopleTreesBuilder(currentDash['selected_items'], state.tree, displayByView)

            // People & Customers contain similar metrics found in Money P&L
            if (!currentDash['selected_items']['people_financial_detail']) {
              peopleFDRows = updatePeopleCustomersChecks(peopleFinancialDetailRows, currentDash['selected_items']['pnl'])
            } else {
              peopleFDRows = peopleFinancialDetailRows
            }

            if (!currentDash['selected_items']['customers_financial_detail']) {
              customersFDRows = updatePeopleCustomersChecks(customersFinancialDetailRows, currentDash['selected_items']['pnl'])
            } else {
              customersFDRows = updateCustomersRows(currentDash['selected_items']['customers_financial_detail'], customersFinancialDetailRows)
            }

            if (!currentDash['selected_items']['customers_kpi']) {
              customersKpiRows = updatePeopleCustomersChecks(customersKpiRows, currentDash['selected_items']['pnl'])
            } else {
              customersKpiRows = updateCustomersRows(currentDash['selected_items']['customers_kpi'], customersKpiRows)
            }

            let customerFDKeys = customersFDRows.map(cfdr => cfdr.key)
            let customersKpiKeys = customersKpiRows.map(ck => ck.key)

            layout = layout.filter(l => {
              if (l.metricRow) {
                return reportMetrics.allKeys.includes(l.i) || reportMetrics.allKeys.includes(l.metricRow?.key)
              } else {
                return true
              }
            })

            let selectedItems = currentDash['selected_items']['money_all_selected_items'].length > 0 ? currentDash['selected_items']['money_all_selected_items'] : peerviewChartsSelected;
            currentDash.selected_items = selectedItems
            currentDash.dashboard_layout = layout
            let naicsSpecific = 'no';
            let parentSpecific = 'no';
            let naicsSearch = [];
            let parentSearch = [];
            let companySearch = [];
            if (currentDash.naics && currentDash.naics.length > 0) {
              naicsSpecific = 'yes'
              naicsSearch = currentDash.naics
            }
            if (currentDash.parent_ids && currentDash.parent_ids.length > 0) {
              parentSpecific = 'yes'
              parentSearch = currentDash.parent_companies
            }
            if (currentDash.company_ids && currentDash.company_ids.length > 0) {
              companySearch = currentDash.companies
            }

            setReportBuilderModalState((prevState) => {
              return {
                ...prevState,
                current_view: currentDash,
                naicsSpecific: naicsSpecific,
                parentSpecific: parentSpecific,
                naicsSearch: naicsSearch,
                parentSearch: parentSearch,
                companySearch: companySearch
              }
            })
            setReportBuilderMetrics(prevState => {
              return {
                ...prevState,
                id: currentDash.id,
                layout: layout,
                rev_and_opex_charts: revOpexCharts,
                peerview_charts: peerviewCharts,
                pnl: pnlOnePageRows,
                balance_sheet: balOnePageRows,
                cash_flow: cashFlowRows,
                kfis: kfisRows,
                people_peerview_charts: peoplePeerviewCharts,
                people_financial_detail: peopleFDRows,
                people_kpi: peopleKpiRows,
                customers_peerview_charts: customersPeerviewCharts,
                customers_financial_detail: customersFDRows,
                customers_kpi: customersKpiRows,
                allSelectedMetrics: selectedItems,
                allSheetMetrics: [...revOpexCharts, ...peerviewCharts, ...pnlOnePageRows, ...balOnePageRows, ...cashFlowRows, ...kfisRows, ...peoplePeerviewCharts, ...peopleFDRows, ...peopleKpiRows, ...customersPeerviewCharts, ...customersFDRows, ...customersKpiRows],
                noChangesMade: true
              }
            })
          }
        } else {
          setReportBuilderModalState(prevState => {
            return {
              ...prevState,
              current_view: currentDash
            }
          })
          // setDashboard(() => {
          //   return {
          //     id: currentDash.id,
          //     selectedItems: currentDash.selected_items,
          //     layout: currentDash.dashboard_layout,
          //     noChangesMade: true
          //   }
          // })
        }
      }
    } catch (error) {
      console.log(error);
      toast.error(`Sorry something went wrong.`, {
        position: 'top-right',
        autoClose: 5000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: false,
        progress: undefined,
      });
    }
  }

  const updateCustomReport = async (e = false, dashId = null, dashViewInfo = null) => {
    if (e) { e.preventDefault() }
    let dashViewId = dashId
    if (!dashViewId) dashViewId = reportBuilderMetrics.id
    let dashboard = reportBuilderModalState.user_views.find(v => v.id === dashViewId)
    let noViewEdit = dashboard.permission_details === 'view_only' && props.user_id !== dashboard.user_id

    if (noViewEdit) {
      toast.error(`Sorry You Are NOT Allowed to Edit this Dashboard View. You need to be the owner of this dashboard view in order to edit.`, {
        position: 'top-right',
        autoClose: 10000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: false,
        progress: undefined,
      });
    } else {

      let updatedView = { report_type: 'money', editor_page: true, admin_custom_default: false, normal_custom: true }

      if (dashViewInfo) {
        if (dashViewInfo.viewApply === 'all_firm_clients_naics') {
          let company = reportBuilderModalState.child_companies_list.find(c => c[0] === companyForm.id)
          dashViewInfo.naics = [company[3]]
        }
        updatedView['name'] = dashViewInfo.name
        updatedView['private'] = dashViewInfo.viewVisibilty === 'only_me'
        updatedView['permission_details'] = dashViewInfo.viewPermission
        updatedView['view_apply'] = dashViewInfo.viewApply
        updatedView['admin_custom_default'] = false
        updatedView['normal_custom'] = true
        updatedView['naics'] = dashViewInfo.viewApply === 'specific_clients' ? null : dashViewInfo.naics
        updatedView['parent_ids'] = dashViewInfo.parent_ids
        updatedView['company_ids'] = dashViewInfo.company_ids
      }

      if (report === 'money' || report === 'analysis') {
        updatedView['selected_items'] = {
          rev_and_opex_charts: reportBuilderMetrics['rev_and_opex_charts'],
          peerview_charts: reportBuilderMetrics['peerview_charts'],
          pnl: reportBuilderMetrics['pnl'],
          balance_sheet: reportBuilderMetrics['balance_sheet'],
          cash_flow: reportBuilderMetrics['cash_flow'],
          kfis: reportBuilderMetrics['kfis'],
          money_all_selected_items: reportBuilderMetrics['allSelectedMetrics'],
          money_user_notes: '',
          people_peerview_charts: reportBuilderMetrics['people_peerview_charts'],
          people_financial_detail: reportBuilderMetrics['people_financial_detail'],
          people_kpi: reportBuilderMetrics['people_kpi'],
          customers_peerview_charts: reportBuilderMetrics['customers_peerview_charts'],
          customers_financial_detail: reportBuilderMetrics['customers_financial_detail'],
          customers_kpi: reportBuilderMetrics['customers_kpi'],
        }
        updatedView['dashboard_layout'] = reportBuilderMetrics['layout']
        setReportBuilderMetrics((prevState) => {
          return { ...prevState, noChangesMade: true }
        })
      }

      try {
        const res = await fetch(`/dashboard_views/${dashViewId}`, {
          method: 'PATCH',
          headers: {
            'X-CSRF-Token': window.token,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(updatedView),
        })
        const dashViewRes = await res.json()

        if (!res.ok) {
          throw new Error('Network response was not ok.');
        } else {
          setReportBuilderModalState(prevState => {
            if (reportBuilderMetrics.id !== dashViewId && (report === 'money' || report === 'analysis')) {
              return {
                ...prevState,
                user_views: dashViewRes.user_views,
              }
            } else {
              return {
                ...prevState,
                current_view: dashViewRes.current_view,
                user_views: dashViewRes.user_views,
              }
            }
          })
          setCustomDashboardViews(prevState => {
            let newByUser = prevState.by_user.map(r => {
              if (r.id === dashId) {
                r['name'] = dashViewInfo.name
              }
              return r
            });

            return {
              ...prevState,
              by_user: newByUser
            }
          })
          toast.success('Changes to view saved!', {
            position: 'top-right',
            autoClose: 2000,
            hideProgressBar: true,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: false,
            progress: undefined,
          });
        }
      } catch (error) {
        console.log(error);
        toast.error('Sorry something went wrong. Changes to view could not be saved.', {
          position: 'top-right',
          autoClose: 2000,
          hideProgressBar: true,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: false,
          progress: undefined,
        });
      }
    }
  }


  const handleReportBuilderModal = (action, info = null) => {
    setReportBuilderModalState(prevState => {
      if (action === 'close') {
        return { ...prevState, isOpen: false, action: 'close' }
      } else if (action === 'edit_view') {
        let currentView = prevState.current_view
        let currentInfo = !info ? currentView : info
        let noViewEdit = currentInfo.permission_details === 'view_only' && props.user_id !== currentInfo.user_id

        if (noViewEdit) {
          toast.error(`Sorry You Are NOT Allowed to Edit this Dashboard View. You need to be the owner of this dashboard view in order to edit.`, {
            position: 'top-right',
            autoClose: 10000,
            hideProgressBar: true,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: false,
            progress: undefined,
          });
          return prevState
        } else {
          let naicsSpecific = 'no';
          let naicsSearch = [];
          let companySearch = [];

          if (currentInfo.naics && currentInfo.naics.length > 0) {
            naicsSpecific = 'yes'
            naicsSearch = currentInfo.naics
          }
          if (currentInfo.company_ids && currentInfo.company_ids.length > 0) {
            companySearch = currentInfo.companies
          }

          return { ...prevState, isOpen: true, action: action, displayViewPermissionQ: currentInfo.private, viewPermission: currentInfo.permission_details, viewApply: currentInfo.view_apply, edit_view: currentInfo, naicsSpecific: naicsSpecific, naicsSearch: naicsSearch, companySearch: companySearch }
        }
      } else {
        return { ...prevState, isOpen: true, action: action, edit_view: {}, naicsSpecific: 'no', parentSpecific: 'no', naicsSearch: [], parentSearch: [], companySearch: [] }
      }
    })
  }

  const getDashBoardView = async (dashViewId) => {
    try {
      const res = await fetch(`/dashboard_views/${dashViewId}`, {
        method: 'GET',
        headers: {
          'X-CSRF-Token': window.token,
          'Content-Type': 'application/json',
        }
      })
      const dashViewRes = await res.json();

      if (!res.ok) {
        throw new Error('Network response was not ok.');
      } else {
        let currentDash = dashViewRes['current_view']
        if (report === 'money' || report === 'analysis' || report === 'people') {
          let allKeys = [];
          let allSelectedKeys = []
          let layout = [];
          let revOpexCharts = moneyCharts['rev_and_opex_charts'];
          let peerviewCharts = null;
          let peerviewChartsSelected = [];
          let peopleLayout = [];
          let peoplePeerviewCharts = [];
          let peoplePeerviewChartsSelected = [];
          let peopleFDRows = []
          let customersLayout = [];
          let customersPeerviewCharts = [];
          let customersPeerviewChartsSelected = [];
          let customersFDRows = []
          let customersKpiRows = customersKPIsFormat();

          if (currentDash?.['selected_items']?.['rev_and_opex_charts']?.length > 0) {
            revOpexCharts = currentDash['selected_items']['rev_and_opex_charts']
          }

          if (currentDash?.['selected_items']?.['peerview_charts']?.length) {
            let { selectedItems } = mergeItems(report, currentDash['selected_items']['peerview_charts'], []);
            peerviewCharts = selectedItems;
            peerviewCharts.forEach((chart) => {
              if (chart.checked) {
                peerviewChartsSelected.push(chart)
              }
            })

            layout = currentDash['dashboard_layout']
          } else {
            peerviewCharts = moneyCharts['peerview_charts']
            peerviewCharts.forEach((chart) => {
              if (chart.checked) {
                let defaultLayout = defaultLayouts['money_peerview_charts'].find((l) => l.i === chart.key)
                if (defaultLayout) {
                  layout.push(defaultLayout)
                  peerviewChartsSelected.push(chart)
                }
              }
            })
          }

          if (currentDash['selected_items'] && currentDash['selected_items']['people_peerview_charts'] && currentDash['selected_items']['people_peerview_charts'].length > 0) {
            let { selectedItems } = mergeItems('people', currentDash['selected_items']['people_peerview_charts'], []);
            peoplePeerviewCharts = selectedItems;
          } else {
            peoplePeerviewCharts = peopleCharts['peerview_charts']
          }

          if (currentDash['selected_items'] && currentDash['selected_items']['customers_peerview_charts'] && currentDash['selected_items']['customers_peerview_charts'].length > 0) {
            let { selectedItems } = mergeItems('customers', currentDash['selected_items']['customers_peerview_charts'], []);
            customersPeerviewCharts = selectedItems;
          } else {
            customersPeerviewCharts = customerCharts['peerview_charts']
          }

          let displayByView = 'yearsView'
          if (filters['display_columns_by'] === 'Years' && filters['report_period'] === 'Year to Date') {
            displayByView = 'ytdView'
          }
          if (filters['display_columns_by'] === 'Months') {
            displayByView = 'monthsView'
          }
          // Money is going to have all people and customers
          if (report === 'money' || report === 'analysis') {
            let { moneyKeys, pnlOnePageRows, balOnePageRows, cashFlowRows, kfisRows } = moneyTreesBuilder(currentDash['selected_items'], state.tree, displayByView)
            let { peopleKeys, peopleFinancialDetailRows, peopleKpiRows } = peopleTreesBuilder(currentDash['selected_items'], state.tree, displayByView)

            // People & Customers contain similar metrics found in Money P&L
            if (!currentDash['selected_items']['people_financial_detail']) {
              peopleFDRows = updatePeopleCustomersChecks(peopleFinancialDetailRows, currentDash['selected_items']['pnl'])
            } else {
              peopleFDRows = peopleFinancialDetailRows
            }

            if (!currentDash['selected_items']['customers_financial_detail']) {
              customersFDRows = updatePeopleCustomersChecks(customersFinancialDetailRows, currentDash['selected_items']['pnl'])
            } else {
              customersFDRows = updateCustomersRows(currentDash['selected_items']['customers_financial_detail'], customersFinancialDetailRows)
            }

            if (!currentDash['selected_items']['customers_kpi']) {
              customersKpiRows = updatePeopleCustomersChecks(customersKpiRows, currentDash['selected_items']['pnl'])
            } else {
              customersKpiRows = updateCustomersRows(currentDash['selected_items']['customers_kpi'], customersKpiRows)
            }

            let customerFDKeys = customersFDRows.map(cfdr => cfdr.key)
            let customersKpiKeys = customersKpiRows.map(ck => ck.key)

            layout = layout.filter(l => {
              if (l.metricRow) {
                return reportMetrics.allKeys.includes(l.i) || reportMetrics.allKeys.includes(l.metricRow?.key)
              } else {
                return true
              }
            })

            let selectedItems = currentDash['selected_items']['money_all_selected_items'].length > 0 ? currentDash['selected_items']['money_all_selected_items'] : peerviewChartsSelected;
            currentDash.selected_items = selectedItems
            currentDash.dashboard_layout = layout

            setDashboardViewModalState((prevState) => {
              return {
                ...prevState,
                current_view: currentDash
              }
            })
            setReportMetrics(prevState => {
              return {
                ...prevState,
                id: currentDash.id,
                layout: layout,
                rev_and_opex_charts: revOpexCharts,
                peerview_charts: peerviewCharts,
                pnl: pnlOnePageRows,
                balance_sheet: balOnePageRows,
                cash_flow: cashFlowRows,
                kfis: kfisRows,
                people_peerview_charts: peoplePeerviewCharts,
                people_financial_detail: peopleFDRows,
                people_kpi: peopleKpiRows,
                customers_peerview_charts: customersPeerviewCharts,
                customers_financial_detail: customersFDRows,
                customers_kpi: customersKpiRows,
                allSelectedMetrics: selectedItems,
                allSheetMetrics: [...revOpexCharts, ...peerviewCharts, ...pnlOnePageRows, ...balOnePageRows, ...cashFlowRows, ...kfisRows, ...peoplePeerviewCharts, ...peopleFDRows, ...peopleKpiRows, ...customersPeerviewCharts, ...customersFDRows, ...customersKpiRows],
                noChangesMade: true
              }
            })
          } else if (report === 'people') {
            let { peopleKeys, peopleFinancialDetailRows, peopleKpiRows } = peopleTreesBuilder(currentDash['selected_items'], state.tree, displayByView)
            let selectedItems = currentDash['selected_items']['all_selected_items'].length > 0 ? currentDash['selected_items']['all_selected_items'] : peerviewChartsSelected;
            currentDash.selected_items = selectedItems
            currentDash.dashboard_layout = layout

            setDashboardViewModalState((prevState) => {
              return {
                ...prevState,
                current_view: currentDash
              }
            })
            setReportMetrics(prevState => {
              return {
                ...prevState,
                id: currentDash.id,
                layout: layout,
                peerview_charts: peerviewCharts,
                financial_detail: peopleFinancialDetailRows,
                kpi: peopleKpiRows,
                allSelectedMetrics: selectedItems,
                allSheetMetrics: [...peerviewCharts, ...peopleFinancialDetailRows, ...peopleKpiRows],
                noChangesMade: true
              }
            })
          }
        } else if (report === 'customers') {
          let finalLayout = [];
          let peerviewCharts = null;
          let peerviewChartsSelected = [];
          if (currentDash['selected_items'] && currentDash['selected_items']['peerview_charts']) {
            let { selectedItems } = mergeItems('customers', currentDash['selected_items']['peerview_charts'], []);
            peerviewCharts = selectedItems;
            peerviewCharts.forEach((chart) => {
              if (chart.checked) {
                peerviewChartsSelected.push(chart)
              }
            })
            finalLayout = [...currentDash['dashboard_layout']]
          } else {
            peerviewCharts = customerCharts['peerview_charts']
            peerviewCharts.forEach((chart) => {
              if (chart.checked) {
                let defaultLayout = defaultLayouts['money_peerview_charts'].find((l) => l.i === chart.key)
                if (defaultLayout) {
                  finalLayout.push(defaultLayout)
                  peerviewChartsSelected.push(chart)
                }
              }
            })
          }

          let kpiRows = customersKPIsFormat();
          let finalSelectedItems = []
          if (currentDash['selected_items'] && !currentDash['selected_items']['all_selected_items']) {
            let currentSelectedItems = currentDash['selected_items']
            let { layout, selectedItems } = mergeItems('customers', currentSelectedItems, dashBoardRes['current_view']['dashboard_layout'])
            finalLayout = layout
            finalSelectedItems = selectedItems
          } else {
            finalSelectedItems = currentDash['selected_items']['all_selected_items']
            finalLayout = currentDash['dashboard_layout']
          }
          let finalFinancialDetailRows = updateCustomersRows(finalSelectedItems, financialDetailRows)
          let finalKpiRows = updateCustomersRows(finalSelectedItems, kpiRows)
          finalLayout = addMetricRow(finalLayout)

          setDashboardViewModalState((prevState) => {
            return {
              ...prevState,
              current_view: currentDash
            }
          })

          setReportMetrics(prevState => {
            return {
              ...prevState,
              id: currentDash.id,
              layout: finalLayout,
              peerview_charts: peerviewCharts,
              financial_detail: finalFinancialDetailRows,
              kpi: finalKpiRows,
              allSelectedMetrics: finalSelectedItems,
              allSheetMetrics: [...peerviewCharts, ...finalFinancialDetailRows, ...finalKpiRows],
              noChangesMade: true
            }
          })

        } else {
          setDashboardViewModalState(prevState => {
            return {
              ...prevState,
              current_view: currentDash
            }
          })
          setDashboard(() => {
            return {
              id: currentDash.id,
              selectedItems: currentDash.selected_items,
              layout: currentDash.dashboard_layout,
              noChangesMade: true
            }
          })
        }
      }
    } catch (error) {
      console.log(error);
      toast.error(`Sorry something went wrong.`, {
        position: 'top-right',
        autoClose: 5000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: false,
        progress: undefined,
      });
    }
  }

  const updateDashBoardView = async (e = false, dashId = null, dashViewInfo = null) => {
    if (e) { e.preventDefault() }
    let dashViewId = dashId
    let updatedView = { report_type: state.report }
    if (dashViewInfo) {
      updatedView['name'] = dashViewInfo.name
      updatedView['private'] = dashViewInfo.viewVisibilty === 'only_me'
      updatedView['permission_details'] = dashViewInfo.viewPermission
    }

    if (report === 'money' || report === 'analysis') {
      if (!dashViewId) dashViewId = reportMetrics.id
      updatedView['selected_items'] = {
        rev_and_opex_charts: reportMetrics['rev_and_opex_charts'],
        peerview_charts: reportMetrics['peerview_charts'],
        pnl: reportMetrics['pnl'],
        balance_sheet: reportMetrics['balance_sheet'],
        cash_flow: reportMetrics['cash_flow'],
        kfis: reportMetrics['kfis'],
        financial_detail: reportMetrics['financial_detail'],
        kpi: reportMetrics['kpi'],
        money_all_selected_items: reportMetrics['allSelectedMetrics'],
        money_user_notes: moneyNotes['userNotes'],
        people_peerview_charts: reportMetrics['people_peerview_charts'],
        people_financial_detail: reportMetrics['people_financial_detail'],
        people_kpi: reportMetrics['people_kpi'],
        customers_peerview_charts: reportMetrics['customers_peerview_charts'],
        customers_financial_detail: reportMetrics['customers_financial_detail'],
        customers_kpi: reportMetrics['customers_kpi'],
      }
      updatedView['dashboard_layout'] = reportMetrics['layout']
      setReportMetrics((prevState) => {
        return { ...prevState, noChangesMade: true }
      })
    } else if (report === 'people' || report === 'customers') {
      if (!dashViewId) dashViewId = reportMetrics.id
      updatedView['selected_items'] = {
        peerview_charts: reportMetrics['peerview_charts'],
        financial_detail: reportMetrics['financial_detail'],
        kpi: reportMetrics['kpi'],
        all_selected_items: reportMetrics['allSelectedMetrics'],
      }
      updatedView['dashboard_layout'] = reportMetrics['layout']
      setReportMetrics((prevState) => {
        return { ...prevState, noChangesMade: true }
      })
    } else {
      if (!dashViewId) dashViewId = dashboard.id
      updatedView['selected_items'] = dashboard.selectedItems
      updatedView['dashboard_layout'] = dashboard.layout
      setDashboard((prevState) => {
        return { ...prevState, noChangesMade: true }
      })
    }
    try {
      const res = await fetch(`/dashboard_views/${dashViewId}`, {
        method: 'PATCH',
        headers: {
          'X-CSRF-Token': window.token,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updatedView),
      })
      const dashViewRes = await res.json()

      if (!res.ok) {
        throw new Error('Network response was not ok.');
      } else {
        setDashboardViewModalState(prevState => {
          if (reportMetrics.id !== dashViewId && (report === 'money' || report === 'analysis')) {
            return {
              ...prevState,
              user_views: dashViewRes.user_views,
              firms_views: dashViewRes.firms_views
            }
          } else if (dashboard.id !== dashViewId && (report !== 'money' || report !== 'analysis')) {
            return {
              ...prevState,
              user_views: dashViewRes.user_views,
              firms_views: dashViewRes.firms_views
            }
          } else {
            return {
              ...prevState,
              current_view: dashViewRes.current_view,
              user_views: dashViewRes.user_views,
              firms_views: dashViewRes.firms_views
            }
          }
        })
        toast.success('Changes to view saved!', {
          position: 'top-right',
          autoClose: 2000,
          hideProgressBar: true,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: false,
          progress: undefined,
        });
      }
    } catch (error) {
      console.log(error);
      toast.error('Sorry something went wrong. Changes to view could not be saved.', {
        position: 'top-right',
        autoClose: 2000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: false,
        progress: undefined,
      });
    }
  }

  const deleteDashBoardView = async (e, dashViewId) => {
    e.preventDefault()
    try {
      const res = await fetch(`/dashboard_views/${dashViewId}`, {
        method: 'DELETE',
        headers: {
          'X-CSRF-Token': window.token,
          'Content-Type': 'application/json',
        }
      })

      if (!res.ok) {
        throw new Error('Network response was not ok.');
      } else {
        let defaultView = dashboardViewModalState.firms_views.find(v => v.name === 'Default')
        let currentViewDeleted = dashViewId === dashboardViewModalState.current_view.id
        setDashboardViewModalState(prevState => {
          let oldUserViews = prevState.user_views
          let newUserViews = oldUserViews.filter(v => v.id !== dashViewId)
          let oldFirmsViews = prevState.firms_views
          let newFirmsViews = oldFirmsViews.filter(v => v.id !== dashViewId)

          if (currentViewDeleted) {
            return {
              ...prevState,
              current_view: defaultView,
              user_views: newUserViews,
              firms_views: newFirmsViews
            }
          } else {
            return {
              ...prevState,
              user_views: newUserViews,
              firms_views: newFirmsViews
            }
          }
        })

        if (report === 'audit' && currentViewDeleted) {
          setDashboard((prevState) => {
            return {
              id: defaultView.id,
              selectedItems: defaultView.selected_items,
              layout: defaultView.dashboard_layout,
              noChangesMade: true
            }
          })
        }

        if ((report === 'money' && currentViewDeleted) || (report === 'analysis' && currentViewDeleted) || (report === 'people' && currentViewDeleted)) {
          let allKeys = [];
          let layout = [];
          let revOpexCharts = moneyCharts['rev_and_opex_charts'];
          let peerviewCharts = null;
          let peerviewChartsSelected = [];
          let peopleLayout = [];
          let peoplePeerviewCharts = [];
          let peoplePeerviewChartsSelected = [];
          let peopleFDRows = []
          let customersLayout = [];
          let customersPeerviewCharts = [];
          let customersPeerviewChartsSelected = [];
          let customersFDRows = []
          let customersKpiRows = customersKPIsFormat();

          if (defaultView?.['selected_items']?.['rev_and_opex_charts']?.length > 0) {
            revOpexCharts = defaultView['selected_items']['rev_and_opex_charts']
          }

          if (defaultView['selected_items']['peerview_charts'].length > 0) {
            let { selectedItems } = mergeItems(report, defaultView['selected_items']['peerview_charts'], []);
            peerviewCharts = selectedItems;
            peerviewCharts.forEach((chart) => {
              if (chart.checked) {
                peerviewChartsSelected.push(chart)
              }
            })
            // let peerviewChartKeys = defaultView['selected_items']['peerview_charts'].map(p => p.key)
            // let pnlKeys = defaultView['selected_items']['pnl'].map(p => p.key)
            // let balKeys = defaultView['selected_items']['balance_sheet'].map(b => b.key)
            // let cashKeys = defaultView['selected_items']['cash_flow'].map(c => c.key)
            // let kfisKeys = defaultView['selected_items']['kfis'].map(k => k.key)

            // let peoplePChartsKeys = defaultView['selected_items']['people_peerview_charts'] ? defaultView['selected_items']['people_peerview_charts'].map(p => p.key) : []
            // let peopleFDKeys = defaultView['selected_items']['people_financial_detail'] ? defaultView['selected_items']['people_financial_detail'].map(b => b.key) : []
            // let peopleKPI = defaultView['selected_items']['people_kpi'] ? defaultView['selected_items']['people_kpi'].map(c => c.key) : []
            // let customersPChartsKeys = defaultView['selected_items']['customers_peerview_charts'] ? defaultView['selected_items']['customers_peerview_charts'].map(k => k.key) : []
            // let customersFD = defaultView['selected_items']['customers_financial_detail'] ? defaultView['selected_items']['customers_financial_detail'].map(b => b.key) : []
            // let customersKPI = defaultView['selected_items']['customers_kpi'] ? defaultView['selected_items']['customers_kpi'].map(c => c.key) : []
            // let allSelectedKeys = [...peerviewChartKeys, ...pnlKeys, ...balKeys, ...cashKeys, ...kfisKeys, ...kpiKeys]
            // let currentDashLayout = defaultView['dashboard_layout'].filter(l => allSelectedKeys.includes(l.i))
            layout = defaultView['dashboard_layout']
          } else {
            peerviewCharts = (report === 'money' || report === 'analysis') ? moneyCharts['peerview_charts'] : peopleCharts['peerview_charts']
            peerviewCharts.forEach((chart) => {
              if (chart.checked) {
                let defaultLayout = (report === 'money' || report === 'analysis') ? defaultLayouts['money_peerview_charts'].find((l) => l.i === chart.key) : defaultLayouts['people_peerview_charts'].find((l) => l.i === chart.key)
                if (defaultLayout) {
                  layout.push(defaultLayout)
                  peerviewChartsSelected.push(chart)
                }
              }
            })
          }

          if (defaultView['selected_items'] && defaultView['selected_items']['people_peerview_charts'] && defaultView['selected_items']['people_peerview_charts'].length > 0) {
            let { selectedItems } = mergeItems('people', defaultView['selected_items']['people_peerview_charts'], []);
            peoplePeerviewCharts = selectedItems;
          } else {
            peoplePeerviewCharts = peopleCharts['peerview_charts']
          }

          if (defaultView['selected_items'] && defaultView['selected_items']['customers_peerview_charts'] && defaultView['selected_items']['customers_peerview_charts'].length > 0) {
            let { selectedItems } = mergeItems('customers', defaultView['selected_items']['customers_peerview_charts'], []);
            customersPeerviewCharts = selectedItems;
          } else {
            customersPeerviewCharts = customerCharts['peerview_charts']
          }

          let displayByView = 'yearsView'
          if (filters['display_columns_by'] === 'Years' && filters['report_period'] === 'Year to Date') {
            displayByView = 'ytdView'
          }
          if (filters['display_columns_by'] === 'Months') {
            displayByView = 'monthsView'
          }

          if (report === 'money' || report === 'analysis') {
            let { moneyKeys, pnlOnePageRows, balOnePageRows, cashFlowRows, kfisRows } = moneyTreesBuilder(defaultView['selected_items'], state.tree, displayByView)
            let { peopleKeys, peopleFinancialDetailRows, peopleKpiRows } = peopleTreesBuilder(defaultView['selected_items'], state.tree, displayByView)

            // People & Customers contain similar metrics found in Money P&L
            if (!defaultView['selected_items']['people_financial_detail']) {
              peopleFDRows = updatePeopleCustomersChecks(peopleFinancialDetailRows, defaultView['selected_items']['pnl'])
            } else {
              peopleFDRows = peopleFinancialDetailRows
            }

            if (!defaultView['selected_items']['customers_financial_detail']) {
              customersFDRows = updatePeopleCustomersChecks(customersFinancialDetailRows, defaultView['selected_items']['pnl'])
            } else {
              customersFDRows = updateCustomersRows(defaultView['selected_items']['customers_financial_detail'], customersFinancialDetailRows)
            }

            if (!defaultView['selected_items']['customers_kpi']) {
              customersKpiRows = updatePeopleCustomersChecks(customersKpiRows, defaultView['selected_items']['pnl'])
            } else {
              customersKpiRows = updateCustomersRows(defaultView['selected_items']['customers_kpi'], customersKpiRows)
            }

            let customerFDKeys = customersFDRows.map(cfdr => cfdr.key)
            let customersKpiKeys = customersKpiRows.map(ck => ck.key)

            layout = layout.filter(l => {
              if (l.metricRow) {
                return reportMetrics.allKeys.includes(l.i) || reportMetrics.allKeys.includes(l.metricRow?.key)
              } else {
                return true
              }
            })

            let selectedItems = defaultView['selected_items']['money_all_selected_items'].length > 0 ? defaultView['selected_items']['money_all_selected_items'] : peerviewChartsSelected;
            defaultView.selected_items = selectedItems
            defaultView.dashboard_layout = layout

            setReportMetrics(prevState => {
              return {
                ...prevState,
                id: defaultView.id,
                layout: layout,
                rev_and_opex_charts: revOpexCharts,
                peerview_charts: peerviewCharts,
                pnl: pnlOnePageRows,
                balance_sheet: balOnePageRows,
                cash_flow: cashFlowRows,
                kfis: kfisRows,
                people_peerview_charts: peoplePeerviewCharts,
                people_financial_detail: peopleFDRows,
                people_kpi: peopleKpiRows,
                customers_peerview_charts: customersPeerviewCharts,
                customers_financial_detail: customersFDRows,
                customers_kpi: customersKpiRows,
                allSelectedMetrics: selectedItems,
                allSheetMetrics: [...revOpexCharts, ...peerviewCharts, ...pnlOnePageRows, ...balOnePageRows, ...cashFlowRows, ...kfisRows, ...peoplePeerviewCharts, ...peopleFDRows, ...peopleKpiRows, ...customersPeerviewCharts, ...customersFDRows, ...customersKpiRows],
                noChangesMade: true
              }
            })

            setDashboardViewModalState((prevState) => {
              return {
                ...prevState,
                current_view: defaultView
              }
            })
          } else if (report === 'people') {
            let { peopleKeys, peopleFinancialDetailRows, peopleKpiRows } = peopleTreesBuilder(currentDash['selected_items'], state.tree, displayByView)
            let selectedItems = currentDash['selected_items']['all_selected_items'].length > 0 ? currentDash['selected_items']['all_selected_items'] : peerviewChartsSelected;
            currentDash.selected_items = selectedItems
            currentDash.dashboard_layout = layout

            setDashboardViewModalState((prevState) => {
              return {
                ...prevState,
                current_view: currentDash
              }
            })
            setReportMetrics(prevState => {
              return {
                ...prevState,
                id: currentDash.id,
                layout: layout,
                peerview_charts: peerviewCharts,
                financial_detail: peopleFinancialDetailRows,
                kpi: peopleKpiRows,
                allSelectedMetrics: selectedItems,
                allSheetMetrics: [...peerviewCharts, ...peopleFinancialDetailRows, ...peopleKpiRows],
                noChangesMade: true
              }
            })
          }
        }

        if (report === 'customers' && currentViewDeleted) {
          let finalLayout = [];
          let peerviewCharts = null;
          let peerviewChartsSelected = [];
          if (defaultView['selected_items']['peerview_charts'].length > 0) {
            let { selectedItems } = mergeItems('customers', defaultView['selected_items']['peerview_charts'], []);
            peerviewCharts.forEach((chart) => {
              if (chart.checked) {
                peerviewChartsSelected.push(chart)
              }
            })
            finalLayout = [...defaultView['dashboard_layout']]
          } else {
            peerviewCharts = customerCharts['peerview_charts']
            peerviewCharts.forEach((chart) => {
              if (chart.checked) {
                let defaultLayout = defaultLayouts['customers_peerview_charts'].find((l) => l.i === chart.key)
                if (defaultLayout) {
                  finalLayout.push(defaultLayout)
                  peerviewChartsSelected.push(chart)
                }
              }
            })
          }

          let kpiRows = customersKPIsFormat();

          let finalSelectedItems = []
          if (defaultView['selected_items'] && !defaultView['selected_items']['all_selected_items']) {
            let currentSelectedItems = defaultView['selected_items']
            let { layout, selectedItems } = mergeItems('customers', currentSelectedItems, dashBoardRes['current_view']['dashboard_layout'])
            finalLayout = layout
            finalSelectedItems = selectedItems
          } else {
            finalSelectedItems = defaultView['selected_items']['all_selected_items']
            finalLayout = dashBoardRes['current_view']['dashboard_layout']
          }
          let finalFinancialDetailRows = updateCustomersRows(finalSelectedItems, financialDetailRows)
          let finalKpiRows = updateCustomersRows(finalSelectedItems, kpiRows)
          finalLayout = addMetricRow(finalLayout)

          setReportMetrics(prevState => {
            return {
              ...prevState,
              id: defaultView.id || 0,
              layout: finalLayout,
              peerview_charts: peerviewCharts,
              financial_detail: finalFinancialDetailRows,
              kpi: finalKpiRows,
              allSelectedMetrics: finalSelectedItems,
              allSheetMetrics: [...peerviewCharts, ...finalFinancialDetailRows, ...finalKpiRows],
              noChangesMade: true
            }
          })

          defaultView.selected_items = finalSelectedItems
          defaultView.dashboard_layout = finalLayout
          setDashboardViewModalState((prevState) => {
            return {
              ...prevState,
              current_view: defaultView
            }
          })
        }


        toast.success('View was deleted!', {
          position: 'top-right',
          autoClose: 2000,
          hideProgressBar: true,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: false,
          progress: undefined,
        });
      }
    } catch (error) {
      console.log(error);
      toast.error('Sorry something went wrong. Try again later.', {
        position: 'top-right',
        autoClose: 2000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: false,
        progress: undefined,
      });
    }
  }

  const handleDashboardViewModal = (action, info = null) => {
    setDashboardViewModalState(prevState => {
      if (action === 'close') {
        return { ...prevState, isOpen: false, action: 'close' }
      } else if (action === 'edit_view') {
        let currentView = prevState.current_view
        let noViewEdit = currentView.permission_details !== 'view_only' || prevState.user_views.find(v => v.id === currentView.id)
        let currentInfo = !info ? currentView : info
        if (!noViewEdit) {
          toast.error(`Sorry You Are NOT Allowed to Edit this Dashboard View. You need to be the owner of this dashboard view in order to edit.`, {
            position: 'top-right',
            autoClose: 10000,
            hideProgressBar: true,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: false,
            progress: undefined,
          });
          return prevState
        } else {
          return { ...prevState, isOpen: true, action: action, edit_view: currentInfo }
        }
      } else {
        return { ...prevState, isOpen: true, action: action, edit_view: {} }
      }
    })
  }

  const handleSurveyModal = (closeOpenSurvey) => {
    setSurveyModalOpen(closeOpenSurvey);
  };

  const resizeChildLayout = (childLayoutWithData) => {
    // How to make sure Parent is tall and wide enough for children so they don't look squished.
    // layout children have h, w, x, y
    let parentH = childLayoutWithData.h
    let childLayout = childLayoutWithData?.layout || []
    const maxY = Math.max(...childLayout.map(c => (c.y + c.h)));

    if (maxY > parentH || maxY < parentH) {
      childLayoutWithData.h = maxY
    }

    return childLayoutWithData;
  }

  const adjustLayoutForCols = (parentCols, childLayoutWithData) => {
    let childLayout = childLayoutWithData || []
    // parentCols is the same as ChildDashboard's cols or width in grid units
    return childLayout.map(item => {
      const maxX = parentCols - item.w;
      // If item is too far right, shift it left
      if (item.x > maxX) {
        return { ...item, x: Math.max(0, maxX) };
      }

      return item;
    });
  }

  const handleCustomLayoutChange = (customKey, dashBoardType, newLayout, parentLayout = null) => {

    if (dashBoardType === 'parentMyDashBoard') {
      setCustomLayouts((prevState) => {
        return prevState.map((l) => {
          if (l.key === customKey) {
            let modifiedLayout = l['dashboard_layout'].map((ol) => {
              let findNewItem = newLayout.find(item => item.i === ol.i)
              if (findNewItem) {
                let newItem = { ...ol, ...findNewItem }
                if (newItem?.layout) {
                  newItem.layout = adjustLayoutForCols(newItem.w, newItem.layout)
                }
                return newItem
              } else {
                return ol;
              }
            })
            l['dashboard_layout'] = modifiedLayout
            l['noChangesMade'] = false
          }
          return l
        })
      })
    }

    if (dashBoardType === 'childMyDashBoard') {
      setCustomLayouts((prevState) => {
        return prevState.map((l) => {
          if (l.key === customKey) {
            let modifiedLayout = _.cloneDeep(l['dashboard_layout'])
            let findParentLayout = modifiedLayout.find(ol => ol.i === parentLayout.key)
            let findParentLayoutIndex = modifiedLayout.findIndex(ol => ol.i === parentLayout.key)
            if (findParentLayout) {
              findParentLayout.layout = findParentLayout.layout.map(pl => {
                let findNewItem = newLayout.find(item => item.i === pl.i)
                if (findNewItem) {
                  return { ...pl, ...findNewItem }
                } else {
                  return pl;
                }
              })
              findParentLayout = resizeChildLayout(findParentLayout)
              modifiedLayout[findParentLayoutIndex] = findParentLayout
            }
            l['dashboard_layout'] = modifiedLayout
            l['noChangesMade'] = false
          }
          return l
        })
      })
    }
  }

  const handleReportBuilderLayoutChange = (dashBoardType, newLayout, parentLayout = null) => {
    let currentView = dashboardViewModalState.current_view
    let noChangesVal = currentView.permission_details === 'view_only' && !dashboardViewModalState.user_views.find(v => v.id === currentView.id)

    if (dashBoardType === 'parentMyDashBoard') {
      setReportBuilderMetrics((prevState) => {
        let oldLayout = prevState.layout;
        let modifiedLayout = oldLayout.map((ol) => {
          let findNewItem = newLayout.find(item => item.i === ol.i)
          if (findNewItem) {
            let newItem = { ...ol, ...findNewItem }
            if (newItem?.layout) {
              newItem.layout = adjustLayoutForCols(newItem.w, newItem.layout)
            }
            return newItem
          } else {
            return ol;
          }
        })
        return { ...prevState, layout: modifiedLayout, noChangesMade: noChangesVal }
      })
    }

    if (dashBoardType === 'childMyDashBoard') {
      setReportBuilderMetrics((prevState) => {
        let modifiedLayout = _.cloneDeep(prevState.layout);
        let findParentLayout = modifiedLayout.find(ol => ol.i === parentLayout.key)
        let findParentLayoutIndex = modifiedLayout.findIndex(ol => ol.i === parentLayout.key)
        if (findParentLayout) {
          findParentLayout.layout = findParentLayout.layout.map(pl => {
            let findNewItem = newLayout.find(item => item.i === pl.i)
            if (findNewItem) {
              return { ...pl, ...findNewItem }
            } else {
              return pl;
            }
          })
          findParentLayout = resizeChildLayout(findParentLayout)
          modifiedLayout[findParentLayoutIndex] = findParentLayout
        }
        return { ...prevState, layout: modifiedLayout, noChangesMade: noChangesVal }
      })
    }
  }

  const handleOnLayoutChange = (dashBoardType, newLayout, parentLayout = null) => {
    let currentView = dashboardViewModalState.current_view
    let noChangesVal = currentView.permission_details === 'view_only' && !dashboardViewModalState.user_views.find(v => v.id === currentView.id)
    if (currentView.name === 'Default' && navbar.user_type === 'Admin') {
      noChangesVal = false
    }

    if (dashBoardType === 'normalDashboard') {
      setDashboard((prevState) => {
        let oldLayout = prevState.layout;
        let modifiedLayout = oldLayout.map((ol) => {
          let findNewItem = newLayout.find(item => item.i === ol.i)
          if (findNewItem) {
            return { ...ol, ...findNewItem }
          } else {
            return ol;
          }
        })

        return { ...prevState, layout: modifiedLayout, noChangesMade: noChangesVal }
      })
    }
    if (dashBoardType === 'parentMyDashBoard') {
      setReportMetrics((prevState) => {
        let oldLayout = prevState.layout;
        let modifiedLayout = oldLayout.map((ol) => {
          let findNewItem = newLayout.find(item => item.i === ol.i)
          if (findNewItem) {
            let newItem = { ...ol, ...findNewItem }
            if (newItem?.layout) {
              newItem.layout = adjustLayoutForCols(newItem.w, newItem.layout)
            }
            return newItem
          } else {
            return ol;
          }
        })
        return { ...prevState, layout: modifiedLayout, noChangesMade: noChangesVal }
      })
    }

    if (dashBoardType === 'childMyDashBoard') {
      setReportMetrics((prevState) => {
        let modifiedLayout = _.cloneDeep(prevState.layout);
        let findParentLayout = modifiedLayout.find(ol => ol.i === parentLayout.key)
        let findParentLayoutIndex = modifiedLayout.findIndex(ol => ol.i === parentLayout.key)
        if (findParentLayout) {
          findParentLayout.layout = findParentLayout.layout.map(pl => {
            let findNewItem = newLayout.find(item => item.i === pl.i)
            if (findNewItem) {
              return { ...pl, ...findNewItem }
            } else {
              return pl;
            }
          })
          findParentLayout = resizeChildLayout(findParentLayout)
          modifiedLayout[findParentLayoutIndex] = findParentLayout
        }
        return { ...prevState, layout: modifiedLayout, noChangesMade: noChangesVal }
      })
    }
  }

  const handleToggleDraggable = (key, dashType = '', parent = null, customKey = '') => {
    let currentView = dashboardViewModalState.current_view
    let noChangesVal = currentView.permission_details === 'view_only' && !dashboardViewModalState.user_views.find(v => v.id === currentView.id)
    if (dashType === 'moneyDashboard') {
      setReportMetrics((prevState) => {
        let newLayout = _.cloneDeep(prevState.layout);

        if (parent) {
          let parentIndex = newLayout.findIndex((l) => l.i === parent?.key);
          if (parentIndex > -1) {
            let childIndex = parent.layout.findIndex((child) => child.key === key)
            if (childIndex > -1) {
              newLayout[parentIndex].isDraggable = !newLayout[parentIndex].isDraggable
              newLayout[parentIndex]['layout'][childIndex].isDraggable = newLayout[parentIndex].isDraggable
            }
          }
        } else {
          let itemIndex = newLayout.findIndex((l) => l.i === key);
          if (itemIndex > -1) {
            newLayout[itemIndex].isDraggable = !newLayout[itemIndex].isDraggable
          }
        }
        return { ...prevState, layout: newLayout, noChangesMade: noChangesVal }
      })
    } else {
      setDashboard((prevState) => {
        let newLayout = _.cloneDeep(prevState.layout);
        let itemIndex = newLayout.findIndex((l) => l.i === key);
        if (itemIndex > -1) {
          newLayout[itemIndex].isDraggable = !newLayout[itemIndex].isDraggable
        }
        return { ...prevState, layout: newLayout, noChangesMade: noChangesVal }
      })
    }
  }

  const handleReportBuilderEditMetrics = (e = false, selectionEditForm = false) => {
    if (e) { e.preventDefault() }

    setReportBuilderMetrics(prevState => {
      if (selectionEditForm) {
        updateCustomReport()
        return {
          ...prevState,
          noChangesMade: true,
        }
      } else {
        if (prevState.editMetrics) {
          updateCustomReport()
        }
        return {
          ...prevState,
          editMetrics: !prevState.editMetrics,
          noChangesMade: true,
        }
      }
    })
  }

  const handleEditMetrics = (e = false, selectionEditForm = false) => {
    if (e) { e.preventDefault() }

    setReportMetrics(prevState => {
      if (selectionEditForm) {
        updateDashBoardView()
        return {
          ...prevState,
          noChangesMade: true,
        }
      } else {
        if (prevState.editMetrics) {
          updateDashBoardView()
        }
        return {
          ...prevState,
          editMetrics: !prevState.editMetrics,
          noChangesMade: true,
        }
      }
    })
  }

  const createNewReportTab = async (e) => {
    e.preventDefault();

    if (tabNameForm.name.length === 0) {
      setTabNameForm(prevState => {
        return {
          ...prevState, error: 'Please enter a name for the new tab.'
        }
      })
    } else {
      try {
        const res = await fetch(`/api/v1/preferences/new_report_tab`, {
          method: 'POST',
          headers: {
            'X-CSRF-Token': window.token,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            active_report_view: activeReportView,
            key: tabNameForm.key,
            name: tabNameForm.name,
            report: tabNameForm.report,
            details: activeTab.details,
            page: props.page
          }),
        })

        const resJson = await res.json();

        if (!res.ok) {
          throw new Error('Network response was not ok.');
        } else {
          const { details: activeFilters, aggregates: activeAggregates } = activeTab;
          const hydratedNewTab = {
            ...resJson.active_view,
            details: activeFilters,
            aggregates: activeAggregates,
            loaded: true,
          };
 
          if (tabNameForm.report === 'company_notes') {
            createCompanyNotes("", resJson.active_view)
          }

          setReportsOnTabs(prev => {
            const deactivated = prev.map(tab => ({ ...tab, active_view: false }));
            const updated = [...deactivated, hydratedNewTab];
            // Sort by tab_order so tabs appear in the right order
            return updated.sort((a, b) => a.tab_order - b.tab_order);
          });

          setAllReportTabs(prev => {
            const deactivated = prev.map(tab => ({ ...tab, active_view: false }));
            const updated = [...deactivated, hydratedNewTab];
            return updated
          });

          setActiveTab(hydratedNewTab);
          closeNewTabFormModal();
          setReportTabsPopoverOpen(false)
          toast.success('New Tab Created!', {
            position: 'top-right',
            autoClose: 2000,
            hideProgressBar: true,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: false,
            progress: undefined,
          });
        }
      } catch (error) {
        console.log(error)
        alert('Sorry something went wrong. Changes were not saved properly.');
      }
    }
  }

  const deleteReportTab = async (id) => {
    let isActiveTab = id === activeTab.id
    try {
      const res = await fetch(`/api/v1/preferences/${id}`, {
        method: 'DELETE',
        headers: {
          'X-CSRF-Token': window.token,
          'Content-Type': 'application/json',
        }
      })

      if (!res.ok) {
        throw new Error('Network response was not ok.');
      } else {
        if (isActiveTab) {
          let newActiveTab = reportsOnTabs.find(tab => tab.id !== id && tab.key !== 'peerview_copilot')
          let updates = [{ id: newActiveTab.id, display_on_tab: newActiveTab.display_on_tab, active_view: true }]
          await updateReportTabs(newActiveTab, false, updates);
          if (!newActiveTab.loaded) {
            let tabFilters = (newActiveTab.details && Object.keys(newActiveTab.details).length > 0) ? { newTab: newActiveTab, filters: newActiveTab.details } : { newTab: newActiveTab, filters: filters };
            await updateState(tabFilters);
          }
        }
        setReportsOnTabs(prevState => {
          return prevState.filter(tab => tab.id !== id);
        });

        setAllReportTabs(prevState => {
          return prevState.filter(tab => tab.id !== id);
        });
      }
    } catch (error) {
      console.log(error)
      alert('Sorry something went wrong. Changes were not saved properly.');
    }
  }

  const handleDefaultDashboardViewTab = (defaultDashboardView, addToTabs = false) => {
    // Create the Preference and Update all the tabs and reports on tabs
    createPreferenceTab(defaultDashboardView)
  }

  const createPreferenceTab = async (defaultDashboardView) => {
    try {
      const res = await fetch(`/api/v1/preferences/new_tab_and_layout`, {
        method: 'POST',
        headers: {
          'X-CSRF-Token': window.token,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          default_dashboard_view: defaultDashboardView,
          active_report_view: activeReportView,
          details: activeTab.details,
          page: props.page
        }),
      })

      const resJson = await res.json();

      if (!res.ok) {
        throw new Error('Network response was not ok.');
      } else {
        setCustomLayouts(prevState => {
          if (!prevState.find(c => c.id === resJson.active_custom_layout.id)) {
            return [...prevState, resJson.active_custom_layout]
          } else {
            return prevState
          }
        })
        setActiveCustomLayout(resJson.active_custom_layout)
        const { details: activeFilters, aggregates: activeAggregates } = activeTab;
        const hydratedNewTab = {
          ...resJson.active_view,
          details: activeFilters,
          aggregates: activeAggregates,
          loaded: true,
        };

        setReportsOnTabs(prev => {
          const deactivated = prev.map(tab => ({ ...tab, active_view: false }));
          const updated = [...deactivated, hydratedNewTab];
          // Sort by tab_order so tabs appear in the right order
          return updated.sort((a, b) => a.tab_order - b.tab_order);
        });

        setAllReportTabs(prev => {
          const deactivated = prev.map(tab => ({ ...tab, active_view: false }));
          const updated = [...deactivated, hydratedNewTab];
          return updated
        });

        setActiveTab(hydratedNewTab);
      }
    } catch (error) {
      console.log(error)
      alert('Sorry something went wrong. Changes were not saved properly.');
    }
  }

  const hideReportFromTabs = async (viewId) => {
    if (reportsOnTabs.length > 1) {
      let updates = [{ id: viewId, display_on_tab: false, active_view: false }]

      setReportsOnTabs(prevState => {
        let newTabs = prevState.filter(v => v.id !== viewId);

        if (activeTab.id === viewId && newTabs.length > 0) {
          newTabs[newTabs.length - 1].active_view = true
          updates.push({ id: newTabs[newTabs.length - 1].id, active_view: true });
          setActiveTab(newTabs[newTabs.length - 1]);
        }

        return newTabs;
      });

      if (activeTab.id == viewId) {
        // If view was an active tab, then needs to set a new tab.
        setAllReportTabs(prevState => {
          return prevState.map(v => {
            if (v.id === viewId) {
              v.active_view = false
              v.display_on_tab = false
            }
            return v
          })
        });
      } else {
        // If view is not active tab
        setAllReportTabs(prevState => {
          return prevState.map(v => {
            if (v.id === viewId) {
              v.display_on_tab = false
            }
            return v
          })
        });
      }

      try {
        const res = await fetch(`/api/v1/preferences/update_views`, {
          method: 'PATCH',
          headers: {
            'X-CSRF-Token': window.token,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            updates: updates
          }),
        })

        if (!res.ok) {
          throw new Error('Network response was not ok.');
        }
      } catch (error) {
        console.log(error)
        alert('Sorry something went wrong. Changes were not saved properly.');
      }
    }
  }

  const addReportToTabs = async (newTab, addToTabs = false) => {
    if (newTab.update) {
      let updates = [{ id: newTab.id, display_on_tab: newTab.display_on_tab, active_view: newTab.active_view }]
      if (addToTabs) {
        updates[0].tab_order = reportsOnTabs.length + 1
      }

      let oldTabId = activeTab.id
      if (oldTabId !== newTab.id) {
        updates.push({ id: oldTabId, display_on_tab: true, active_view: false });
      }
      await updateReportTabs(newTab, addToTabs, updates);

      if (!newTab.loaded && newTab.key !== 'company_notes') {
        let tabFilters = (newTab.details && Object.keys(newTab.details).length > 0) ? { newTab: newTab, filters: newTab.details } : { newTab: newTab, filters: filters };
        await updateState(tabFilters);
      }
    }
  }

  const updateReportTabs = async (newTab, addToTabs, updates) => {
    let getCustomReports = false

    if (!reportBuilderModalState.calledAlready && newTab.key === 'report_builder') {
      getCustomReports = true
    }
    try {
      const [res, customReports] = await Promise.all([
        fetch(`/api/v1/preferences/update_views`, {
          method: 'PATCH',
          headers: {
            'X-CSRF-Token': window.token,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            updates: updates
          }),
        }),
        getCustomReports
          ? fetch(`/get_custom_reports`, {
            headers: {
              'X-CSRF-Token': window.token,
            }
          })
          : Promise.resolve(null)
      ]);
      if (!res.ok) {
        throw new Error('Network response was not ok.');
      } else {
        if (newTab.report === 'company_notes') {
          setActiveCompanyNote(prevState => {
            const newNote = companyNotes.find(n => n.preference_id == newTab.id)
            if (newNote) {
              return newNote
            }
          })
        }
        const customReportsRes = customReports ? await customReports.json() : null;
        let reportBuilderCurrentDash = {
          selected_items: {
            money_all_selected_items: reportBuilderMetrics['allSelectedMetrics'],
            rev_and_opex_charts: reportBuilderMetrics['rev_and_opex_charts'],
            peerview_charts: reportBuilderMetrics['peerview_charts'],
            pnl: reportBuilderMetrics['pnl'],
            balance_sheet: reportBuilderMetrics['balance_sheet'],
            cash_flow: reportBuilderMetrics['cash_flow'],
            kfis: reportBuilderMetrics['kfis'],
            people_peerview_charts: reportBuilderMetrics['people_peerview_charts'],
            people_financial_detail: reportBuilderMetrics['people_financial_detail'],
            people_kpi: reportBuilderMetrics['people_kpi'],
            customers_peerview_charts: reportBuilderMetrics['customers_peerview_charts'],
            customers_financial_detail: reportBuilderMetrics['customers_financial_detail'],
            customers_kpi: reportBuilderMetrics['customers_kpi'],
          },
          dashboard_layout: reportBuilderMetrics['layout']
        }

        if (customReportsRes && customReportsRes.current_view?.selected_items && customReportsRes.current_view?.selected_items?.peerview_charts) {
          setReportBuilderModalState(prevState => {
            return {
              ...prevState,
              ...customReportsRes,
              calledAlready: true
            }
          })
          reportBuilderCurrentDash = customReportsRes.current_view
          buildDashboardMetrics('report_builder', customReportsRes, state.tree, reportBuilderCurrentDash, setReportBuilderMetrics, setReportBuilderModalState)
        }

        if (addToTabs) {
          setReportsOnTabs(prevState => {
            let updatedViews = prevState.map((v, i) => {
              v.active_view = false
              v.tab_order = i + 1
              return v
            })
            return [...updatedViews, newTab]
          });
        } else {
          setReportsOnTabs(prevState => {
            return prevState.map(v => {
              if (v.id == newTab.id) {
                return newTab
              } else {
                v.active_view = false
                return v
              }
            })
          });
        }

        setAllReportTabs(prevState => {
          const updatedTabs = prevState.map(v => {
            if (v.id === newTab.id) {
              return { ...newTab };
            } else {
              return { ...v, active_view: false };
            }
          });
          return updatedTabs;
        });
        setActiveTab(newTab)

        setState(prevState => {
          let tabFilters = (newTab.details && Object.keys(newTab.details).length > 0) ? newTab.details : filters;
          return {
            ...prevState,
            filters: tabFilters
          }
        })
      }
    } catch (error) {
      console.log(error)
      alert('Sorry something went wrong. Changes were not saved properly.');
    }
  }

  const saveTabOrder = async (newTabs) => {
    let updates = []
    let newTabsOrder = newTabs.map((t, i) => {
      t.tab_order = i + 1
      updates.push({ id: t.id, tab_order: t.tab_order })
      return t
    })
    setReportsOnTabs(newTabsOrder);

    try {
      const res = await fetch("/api/v1/preferences/update_views", {
        method: "PATCH",
        headers: {
          'X-CSRF-Token': window.token,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          updates: updates
        }),
      });

      if (!res.ok) {
        throw new Error('Network response was not ok.');
      } else {
        setReportsOnTabs(newTabsOrder)
      }
    } catch (error) {
      console.error("Error saving tab order:", error);
    }
  }

  const handleSurveySubmit = async (e, surveyState) => {
    e.preventDefault();
    let answers = {}
    let numberYearAnswers = {}
    surveyData.sections.forEach((section, i) => {
      surveyState.surveyLayoutData[section]['sectionData'].forEach((data => {
        if (data.inputType === 'radio' || data.inputType === 'ranking') {
          data.questions.forEach(q => {
            answers[q.key] = q.answer
          })
        } else {
          answers[data.key] = data.answer
          numberYearAnswers[data.key] = data.answer
        }
      }))
    })

    try {
      const res = await fetch(`/api/v1/surveys/${state.companyReportSurvey.survey.id}`, {
        method: 'PATCH',
        headers: {
          'X-CSRF-Token': window.token,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          answers: answers,
          report: state.report,
          year: state.companyReportSurvey.survey.year,
          filters: state.filters
        }),
      })

      const surveyRes = await res.json();

      if (!res.ok) {
        throw new Error('Network response was not ok.');
      } else {
        setState(prevState => {
          return {
            ...prevState,
            companyReportSurvey: {
              survey: surveyRes.companyReportSurvey,
              number_year_inputs: numberYearAnswers
            },
            surveyResults: surveyRes.surveyResults,
            surveyTaken: surveyRes.surveyTaken,
          }
        });
        setSurveyModalOpen(false)
        ahoy.track('Survey Submitted', { survey: `${props.client_name} (NAICS: ${props.code}): ${state.pageTitle} ${state.companyReportSurvey.survey.year}` });
      }
    } catch (error) {
      console.log(error);
      alert(
        'Sorry something went wrong. Changes were not saved properly.'
      );
    }
  }

  const getMonthlyForecastScenario = async (data) => {
    setState((prevState) => {
      return {
        ...prevState,
        loading: true
      }
    })
    let scenarioData = data.split(':')
    if (data !== 'YOY:Default' && data !== 'Peers:Default') {
      const json = await fetch(`/api/v1/monthly_calculations/?monthly_forecast_scenario=${scenarioData[0]}&year=${scenarioData[1]}`, {
        method: 'GET',
        headers: {
          'X-CSRF-Token': window.token,
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      })
      const res = await json.json()
      setState(prevState => {
        return {
          ...prevState,
          baseCalc: res.baseCalc,
          monthlyForecastCalcs: res.monthlyForecastCalcs,
          filters: { ...prevState.filters, compare_with: data },
          loading: false
        }
      })
    }
    setState((prevState) => {
      return {
        ...prevState,
        filters: { ...prevState.filters, compare_with: data },
        loading: false
      }
    })
  }

  const changeChat = (e, chat) => {
    if (chat.id) {
      let newChat = allChats.find((c) => c.id === chat.id)
      newChat.messages = newChat.messages.map(m => {
        m.typewriter = false
        return m
      })
      setChat(newChat)
    } else {
      setChat({})
    }
  }

  const handleCopilotCall = (e) => {
    fetchCopilot(e)
    subscribeCopilot()
  }

  const fetchCopilot = async (e) => {
    setChatLoading(true)
    e.preventDefault()
    setChat({
      id: null,
      title: "Analyze my Data!",
      messages: [
        {
          role: 'user',
          message_type: 'question',
          display_content: "Analyze my Data!"
        }
      ]
    })
    let copilotTab = reportsOnTabs.find(t => t.key === 'peerview_copilot') 
    let copilotFilters = copilotTab ?  copilotTab.details : state.default_filters

    try {
      const response = await fetch('/api/v1/copilot', {
        method: 'POST',
        headers: {
          'X-CSRF-Token': window.token,
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          filters: copilotFilters
        }),
      })
      const res = await response.json()
      console.log(res, 'response')
      setJobs(prevState => {
        return {
          subscribe: true,
          id: res.id
        }
      })

      subscribeCopilot(res.id)
    } catch (error) {
      console.log(error, 'error')
    }
  }

  const subscribeCopilot = async (chat_id) => {
    App[`copilot_${chat_id}`] = App.cable.subscriptions.create(
      {
        channel: 'JobsChannel',
        job_type: 'CopilotWorker',
      },
      {
        received: (res) => {
          console.log(res, 'res1')
          if (res.error) {
            setJobs(prevState => {
              return {
                ...prevState,
                subscribe: false
              }
            })
            setChatLoading(false)
            setChatLoader(
              {
                progress: 0,
                message: 'Initializing analysis process...',
                error: res.error
              }
            )
            console.log(res, 'res.error');
          } else if (res.progress) {
            setChatLoader(
              {
                progress: res.progress,
                message: res.message,
                error: null
              }
            )
          } else {
            if (res.finish) {
              setChat(res.data)
              setChatLoading(false)
              setChatLoader(
                {
                  progress: 0,
                  message: 'Initializing analysis process...',
                  error: null
                }
              )
              console.log(res, 'res2')
              App[`copilot_${chat_id}`].unsubscribe();
              setJobs(prevState => {
                return {
                  ...prevState,
                  subscribe: false
                }
              })
              setAllChats((prevChats) => {
                let newData = res.data
                const updatedChats = [newData, ...prevChats];
                // Remove duplicates based on `id`
                const uniqueChats = updatedChats.filter((chat, index, self) => index === self.findIndex((c) => c.id === chat.id));
                return uniqueChats;
              });
            }
          }
        }
      }
    )
  }

  const updatePeopleCustomersChecks = (updatingRows, referenceRows) => {
    updatingRows.forEach(p => {
      let moneyItem = referenceRows.find(item => item.key === p.key)
      if (moneyItem && moneyItem.checked) {
        p.checked = true
        p.layout = p.layout.map(l => {
          let lItem = moneyItem.layout.find(m => m.key === l.key)
          if (lItem && lItem.checked) {
            l.checked = true
          }
          return l
        })
      }
    })

    return updatingRows
  }

  const mergeMonthlyData = (existing, incoming) => {
    const merged = { ...existing };

    Object.keys(incoming).forEach((key) => {
      merged[key] = merged[key] || {};

      Object.keys(incoming[key]).forEach((year) => {
        merged[key][year] = {
          ...(merged[key][year] || {}),
          ...incoming[key][year]
        };
      });
    });

    return merged;
  };

  const monthlyForecastFormatter = (forecasts, compare_with) => {
    const forecastVals = {};
    const pnlValueSet = [];
    const balValueSet = [];
    // Follow same logic used in buildSheetData in MonthlyForecast
    const forecastSource = forecasts;
    const forecastScenarios = Array.isArray(forecastSource) ? forecastSource : [];
    const baseForecast = forecastScenarios[0] || null;
    const realForecastName = baseForecast?.scenario_name || '';
    const forecastName = compare_with && compare_with.includes("Forecast") ? compare_with : "Default";

    if (baseForecast) {
      let forecastYear = forecastScenarios[0].year
      let balanceSheetBase = new BalanceSheetValues(
        baseForecast.state.balance_sheet,
        baseForecast.state.balance_sheet.children?.net_worth?.operating_expenses_net_income,
        baseForecast.state.pnl.children?.total_other_costs?.depreciation_and_amortization,
      );
      const originalDepreciation = baseForecast.state.balance_sheet.children.total_fixed_assets.less_accumulated_depreciation;
      forecastScenarios.forEach((forecast, index) => {
        const pnlVals = new PnlValues(forecast, 0, null, false, false);
        pnlValueSet.push(pnlVals);
        const balVals = new BalanceSheetValues(
          forecast.state.balance_sheet,
          pnlVals.operating_expenses_net_income,
          pnlVals.depreciation_and_amortization,
          balanceSheetBase,
          forecast.month,
          originalDepreciation,
          null,
          'year1',
        );
        balValueSet.push(balVals);
        balanceSheetBase = balVals;
      });

      // Build forecastVals into object with metric objects of monthls like calcs.you, calcs.avg, etc.
      // Special case for Straightline scenario: PNL's net income should be the same for each month
      const straightLinePnlNetIncome = 0;

      pnlValueSet.forEach((forecastObj, index) => {
        Object.keys(forecastObj).forEach((metric) => {
          // Ensure forecastVals[metric] exists
          if (!forecastVals[metric]) forecastVals[metric] = {};
          // Ensure forecastVals[metric][forecastYear] exists
          if (!forecastVals[metric][forecastYear]) forecastVals[metric][forecastYear] = {};
          // Set month (index + 1 = month)
          forecastVals[metric][forecastYear][index + 1] = forecastObj[metric];
        });
      });

      balValueSet.forEach((forecastObj, index) => {
        Object.keys(forecastObj).forEach((metric) => {
          if (metric === 'operating_expenses_net_income') return;
          if (!forecastVals[metric]) forecastVals[metric] = {};
          if (!forecastVals[metric][forecastYear]) forecastVals[metric][forecastYear] = {};
          forecastVals[metric][forecastYear][index + 1] = forecastObj[metric];
        });
      });
    }

    return { [forecastName]: { name: realForecastName, data: forecastVals } }
  }

  const buildDashboardMetrics = (type, response, tree, currentDash, setStateCallback, dashboardViewModalCallback) => {
    let allKeys = [];
    let layout = [];
    let revOpexCharts = moneyCharts['rev_and_opex_charts']
    let revOpexKeys = moneyCharts['rev_and_opex_charts'].map(m => m.key)
    let peerviewCharts = null;
    let peerviewMoneyKeys = moneyCharts['peerview_charts'].map(m => m.key)
    let peerviewPeopleKeys = peopleCharts['peerview_charts'].map(p => p.key)
    let peerviewCustomersKeys = customerCharts['peerview_charts'].map(c => c.key)
    let peerviewChartsSelected = [];
    let peopleLayout = [];
    let peoplePeerviewCharts = [];
    let peoplePeerviewChartsSelected = [];
    let peopleFDRows = []
    let customersLayout = [];
    let customersPeerviewCharts = [];
    let customersPeerviewChartsSelected = [];
    let customersFDRows = []
    let customersKpiRows = customersKPIsFormat();

    if (currentDash?.['selected_items']?.['rev_and_opex_charts']?.length > 0) {
      revOpexCharts = currentDash['selected_items']['rev_and_opex_charts']
    }

    if (currentDash['selected_items'] && currentDash['selected_items']['peerview_charts'].length > 0) {
      let { selectedItems } = mergeItems('money', currentDash['selected_items']['peerview_charts'], []);
      peerviewCharts = selectedItems;
      peerviewCharts.forEach((chart) => {
        if (chart.checked) {
          peerviewChartsSelected.push(chart)
        }
      })
      layout = currentDash['dashboard_layout']
    } else {
      peerviewCharts = moneyCharts['peerview_charts']
      peerviewCharts.forEach((chart) => {
        if (chart.checked) {
          let defaultLayout = defaultLayouts['money_peerview_charts'].find((l) => l.i === chart.key)
          if (defaultLayout) {
            layout.push(defaultLayout)
            peerviewChartsSelected.push(chart)
          }
        }
      })
    }

    if (currentDash['selected_items'] && currentDash['selected_items']['people_peerview_charts'] && currentDash['selected_items']['people_peerview_charts'].length > 0) {
      let { selectedItems } = mergeItems('people', currentDash['selected_items']['people_peerview_charts'], []);
      peoplePeerviewCharts = selectedItems;
    } else {
      peoplePeerviewCharts = peopleCharts['peerview_charts']
    }

    if (currentDash['selected_items'] && currentDash['selected_items']['customers_peerview_charts'] && currentDash['selected_items']['customers_peerview_charts'].length > 0) {
      let { selectedItems } = mergeItems('customers', currentDash['selected_items']['customers_peerview_charts'], []);
      customersPeerviewCharts = selectedItems;
    } else {
      customersPeerviewCharts = customerCharts['peerview_charts']
    }

    let { moneyKeys, pnlOnePageRows, balOnePageRows, cashFlowRows, kfisRows } = moneyTreesBuilder(currentDash['selected_items'], tree)
    let { peopleKeys, peopleFinancialDetailRows, peopleKpiRows } = peopleTreesBuilder(currentDash['selected_items'], tree)

    // People & Customers contain similar metrics found in Money P&L
    if (!currentDash['selected_items']['people_financial_detail']) {
      peopleFDRows = updatePeopleCustomersChecks(peopleFinancialDetailRows, currentDash['selected_items']['pnl'])
    } else {
      peopleFDRows = peopleFinancialDetailRows
    }

    if (!currentDash['selected_items']['customers_financial_detail']) {
      customersFDRows = updatePeopleCustomersChecks(customersFinancialDetailRows, currentDash['selected_items']['pnl'])
    } else {
      customersFDRows = updateCustomersRows(currentDash['selected_items']['customers_financial_detail'], customersFinancialDetailRows)
    }

    if (!currentDash['selected_items']['customers_kpi']) {
      customersKpiRows = updatePeopleCustomersChecks(customersKpiRows, currentDash['selected_items']['pnl'])
    } else {
      customersKpiRows = updateCustomersRows(currentDash['selected_items']['customers_kpi'], customersKpiRows)
    }

    let customerFDKeys = customersFDRows.map(cfdr => cfdr.key)
    let customersKpiKeys = customersKpiRows.map(ck => ck.key)
    allKeys = [...revOpexKeys, ...peerviewMoneyKeys, ...peerviewPeopleKeys, ...peerviewCustomersKeys, ...moneyKeys, ...peopleKeys, ...customerFDKeys, ...customersKpiKeys]

    let selectedItems = currentDash['selected_items']['money_all_selected_items'].length > 0 ? currentDash['selected_items']['money_all_selected_items'] : peerviewChartsSelected;

    layout = layout.filter(l => {
      if (l.metricRow) {
        return allKeys.includes(l.i) || allKeys.includes(l.metricRow?.key)
      } else {
        return true
      }
    })

    setStateCallback(prevState => {
      return {
        ...prevState,
        id: currentDash.id || 0,
        allKeys: allKeys,
        layout: layout,
        rev_and_opex_charts: revOpexCharts,
        peerview_charts: peerviewCharts,
        pnl: pnlOnePageRows,
        balance_sheet: balOnePageRows,
        cash_flow: cashFlowRows,
        kfis: kfisRows,
        people_peerview_charts: peoplePeerviewCharts,
        people_financial_detail: peopleFDRows,
        people_kpi: peopleKpiRows,
        customers_peerview_charts: customersPeerviewCharts,
        customers_financial_detail: customersFDRows,
        customers_kpi: customersKpiRows,
        allSelectedMetrics: selectedItems,
        allSheetMetrics: [...revOpexCharts, ...peerviewCharts, ...pnlOnePageRows, ...balOnePageRows, ...cashFlowRows, ...kfisRows, ...peoplePeerviewCharts, ...peopleFDRows, ...peopleKpiRows, ...customersPeerviewCharts, ...customersFDRows, ...customersKpiRows],
        noChangesMade: true
      }
    })
    currentDash.selected_items = selectedItems
    currentDash.dashboard_layout = layout

    if (type === 'dashboard') {
      dashboardViewModalCallback((prevState) => {
        let userViews = response ? response.user_views : prevState.user_views;
        let firmViews = response ? response.firms_views : prevState.firms_views;
        return {
          ...prevState,
          current_view: currentDash,
          user_views: userViews,
          firms_views: firmViews,
          loaded: true
        }
      })
    } else {
      dashboardViewModalCallback((prevState) => {
        let currentView = { ...prevState.current_view, ...currentDash }
        return {
          ...prevState,
          current_view: currentView,
        }
      })
    }
  }

  const areFiltersEqual = (filtersA, filtersB) => {
    if (!filtersA || !filtersB) return false;

    const keysToCompare = [
      'code',
      'compare_with',
      'datatype',
      'display_columns_by',
      'entity_type',
      'number_of_pros',
      'region',
      'report_period',
      'revenue_band',
      'year',
      'date_through_monthly_month',
      'date_through_monthly_year'
    ];

    return keysToCompare.every(key => filtersA[key] === filtersB[key]);
  };

  const mergeYouCalcs = (existingCalcs, newData) => {
    if (existingCalcs) {
      const updatedCalcs = { ...existingCalcs };

      if (!newData?.you) return updatedCalcs;
      if (newData?.recent_month) {
        updatedCalcs.recent_month = newData.recent_month;
      }

      updatedCalcs.you = { ...updatedCalcs.you }; // clone to avoid mutation
      updatedCalcs.you_by_class = { ...updatedCalcs.you_by_class }

      const existingRevenueYears = existingCalcs.you?.total_revenue
        ? Object.keys(existingCalcs.you.total_revenue)
        : [];
      const incomingRevenueYears = Object.keys(newData?.you?.total_revenue || {});
      const missingYears = incomingRevenueYears.filter(year => !existingRevenueYears.includes(year));
      if (missingYears.length === 0) return updatedCalcs;

      for (const metric in newData.you) {
        const newMetricData = newData.you[metric];
        const existingMetricData = updatedCalcs.you[metric] || {};
        const merged = { ...existingMetricData };

        for (const year of missingYears) {
          if (year in newMetricData) {
            merged[year] = newMetricData[year];
          }
        }

        updatedCalcs.you[metric] = merged;
      }
      
      updatedCalcs.you_by_class = { ...updatedCalcs.you_by_class, ...newData.you_by_class}

      return updatedCalcs;
    } else {
      return newData;
    }
  }

  const tabsToUpdateCheck = (applyTabs, singleTab = null, filters) => {
    let tabsUpdate = []
    if (applyTabs === 'applyToAllTabs') {
      tabsUpdate = allReportTabs.map(t => t.id)
    } else if (singleTab && applyTabs === 'applyToThisTab') {
      tabsUpdate = [singleTab]
    }
    let noFilterTabs = allReportTabs.filter(t => !t.details)
    let noFilterTabIds = noFilterTabs.map(t => t.id)
    return [...tabsUpdate, ...noFilterTabIds]
  }

  // Fetch Dashboard layouts based on report type, years vs monthly/ytd, displaying which charts on dashboard
  const fetchCalcs = async (args, { report, ...prevState }, isReportBuilderTabActive) => {
    // save any checked metrics and dashboard changes
    if (reportMetrics.editMetrics) { handleEditMetrics() }
    let previousFilterOptions, previousNaicsCode, previousRegion, previousRevenueBand, previousYear;
    let survey = null;
    let surveyCharts = [];
    let newTab = args.newTab || activeTab;
    const newFilters = args.filters || {};

    if (Object.keys(prevState.filterOptions).length > 0) {
      previousFilterOptions = prevState.filterOptions;
      previousNaicsCode = previousFilterOptions.code;
      previousRegion = previousFilterOptions.region;
      previousRevenueBand = previousFilterOptions.revenue_band;
      previousYear = previousFilterOptions.year;
    }
    let knownYears = trendData?.['total_revenue'] ? Object.keys(trendData['total_revenue']) : []
    let getDefaultFilters = Object.keys(state.default_filters).length === 0;
    let tabsToUpdate = tabsToUpdateCheck(args.applyTabs, newTab.id, newFilters)

    try {
      const [calculations, dashBoard, customReports] = await Promise.all([
        fetch('/api/v1/calculations', {
          method: 'POST',
          headers: {
            'X-CSRF-Token': window.token,
            'Accept': 'application/json',
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            company_id: props.id,
            known_years: knownYears,
            tabsToUpdate: tabsToUpdate,
            get_default_filters: getDefaultFilters,
            filters: newFilters,
            report,
            ...args // over write current state with new args
          }),
        }),
        !dashboardViewModalState.loaded
          ? fetch(`/get_dashboard_view/?report_type=${report}`, {
            headers: {
              'X-CSRF-Token': window.token,
            }
          })
          : Promise.resolve(null),
        isReportBuilderTabActive
          ? fetch(`/get_custom_reports`, {
            headers: {
              'X-CSRF-Token': window.token,
            }
          })
          : Promise.resolve(null)
      ]);
      const calcsRes = await calculations.json();
      const dashBoardRes = !dashboardViewModalState.loaded ? await dashBoard.json() : null;
      const customReportsRes = customReports ? await customReports.json() : null;
      const { reportSpecific, filterOptions, calcs, monthly_calcs, trailing12, ...newState } = calcsRes
      let formattedMonthlyForecast = {}
      if (monthly_calcs?.forecast_default) {
        formattedMonthlyForecast = monthlyForecastFormatter(monthly_calcs?.forecast_default, newFilters.compare_with)
      }

      let currentDash = !dashboardViewModalState.loaded ? dashBoardRes['current_view'] : dashboardViewModalState.current_view
      let newTrendData = mergeMonthlyData(trendData, newState.trendData);
      const finalFilterOptions = {
        compare_with: filterOptions.compare_with,
        display_columns_by: filterOptions.display_columns_by,
      };
      if (filterOptions && filterOptions['number_of_pros']) { finalFilterOptions['number_of_pros'] = filterOptions['number_of_pros'] }
      let finalNaicsCode, finalRegion, finalRevenueBand, finalYear;
      let naicsCode = filterOptions.code;
      let region = filterOptions.region;
      let revenueBand = filterOptions.revenue_band;
      let year = filterOptions.year;

      if (Object.keys(prevState.filterOptions).length > 0) {
        let mergedNaicsCodes = [...previousNaicsCode, ...naicsCode]
        let previousCodeNumbers = previousNaicsCode.map((n) => n[0])
        let newCodeNumbers = naicsCode.map((n) => n[0])
        let allNaicsCodeNumbers = previousCodeNumbers.concat(newCodeNumbers.filter((n) => !previousCodeNumbers.includes(n)))
        let sortedNaicsCodes = allNaicsCodeNumbers.sort()
        finalNaicsCode = sortedNaicsCodes.map((n) => {
          let naicsCode = mergedNaicsCodes.find((mc) => mc[0] === n)
          return naicsCode
        })
        finalRegion = previousRegion.concat(region.filter((r) => !previousRegion.includes(r)))
        finalRevenueBand = previousRevenueBand.concat(revenueBand.filter((rb) => !previousRevenueBand.includes(rb)))
        finalYear = previousYear.concat(year.filter((y) => !previousYear.includes(y)))
        finalFilterOptions.code = finalNaicsCode
        finalFilterOptions.region = finalRegion;
        finalFilterOptions.revenue_band = finalRevenueBand;
        finalFilterOptions.year = finalYear;
      } else {
        finalFilterOptions.code = naicsCode;
        finalFilterOptions.region = region;
        finalFilterOptions.revenue_band = revenueBand;
        finalFilterOptions.year = year;
      }

      let reportBuilderCurrentDash = {
        selected_items: {
          money_all_selected_items: reportBuilderMetrics['allSelectedMetrics'],
          rev_and_opex_charts: reportBuilderMetrics['rev_and_opex_charts'],
          peerview_charts: reportBuilderMetrics['peerview_charts'],
          pnl: reportBuilderMetrics['pnl'],
          balance_sheet: reportBuilderMetrics['balance_sheet'],
          cash_flow: reportBuilderMetrics['cash_flow'],
          kfis: reportBuilderMetrics['kfis'],
          people_peerview_charts: reportBuilderMetrics['people_peerview_charts'],
          people_financial_detail: reportBuilderMetrics['people_financial_detail'],
          people_kpi: reportBuilderMetrics['people_kpi'],
          customers_peerview_charts: reportBuilderMetrics['customers_peerview_charts'],
          customers_financial_detail: reportBuilderMetrics['customers_financial_detail'],
          customers_kpi: reportBuilderMetrics['customers_kpi'],
        },
        dashboard_layout: reportBuilderMetrics['layout']
      }

      if (customReportsRes && customReportsRes.current_view?.selected_items && customReportsRes.current_view?.selected_items?.peerview_charts) {
        setReportBuilderModalState(prevState => {
          return {
            ...prevState,
            ...customReportsRes,
            calledAlready: true
          }
        })
        reportBuilderCurrentDash = customReportsRes.current_view
      }
      buildDashboardMetrics('report_builder', customReportsRes, newState.tree, reportBuilderCurrentDash, setReportBuilderMetrics, setReportBuilderModalState)

      if ((report === 'money' || report === 'analysis') && dashBoardRes) {
        buildDashboardMetrics('dashboard', dashBoardRes, newState.tree, currentDash, setReportMetrics, setDashboardViewModalState)
        setMoneyNotes(prevState => {
          return {
            userNotes: dashBoardRes['money_notes'] ? dashBoardRes['money_notes'] : prevState.userNotes
          }
        })

      } else if (report === 'audit') {
        let { pnlOnePageRows, balOnePageRows, kfisRows } = auditTreesBuilder(newState.tree)

        setReportMetrics(prevState => {
          return {
            ...prevState,
            pnl: pnlOnePageRows,
            balance_sheet: balOnePageRows,
            kfis: kfisRows,
            allSelectedMetrics: [],
            allSheetMetrics: [...pnlOnePageRows, ...balOnePageRows],
            noChangesMade: true
          }
        })
      } else {
        setDashboard(() => {
          return {
            id: currentDash.id,
            selectedItems: currentDash.selected_items,
            layout: currentDash.dashboard_layout,
            noChangesMade: true
          }
        })

        setDashboardViewModalState((prevState) => {
          return {
            ...prevState,
            current_view: currentDash,
            user_views: dashBoardRes ? dashBoardRes?.user_views : prevState.user_views,
            firms_views: dashBoardRes ? dashBoardRes?.firms_views : prevState.firms_views
          }
        })
      }

      let aggregateCalcs = {
        annual_n: newState?.annual_n || null,
        annual_docs_n: newState?.annual_docs_n || null,
        monthly_avg_n: newState?.monthly_avg_n || null,
        monthly_docs_avg_n: newState?.monthly_docs_avg_n || null,
        annual: {
          avg: calcs?.avg || null,
          docs_avg: calcs?.docs_avg || null,
          practice_avg: calcs?.practice_avg || null,
          nschbc: calcs?.nschbc || null,
          top_10_revenue_growth: calcs?.top_10_revenue_growth || null,
          top_25_revenue_growth: calcs?.top_25_revenue_growth || null,
          top_10_net_profit: calcs?.top_10_net_profit || null,
          top_25_net_profit: calcs?.top_25_net_profit || null,
        },
        monthly: {
          avg: monthly_calcs?.avg || null,
          docs_avg: monthly_calcs?.docs_avg || null,
        },
        trailing12: {
          avg: trailing12?.avg || null,
          docs_avg: trailing12?.docs_avg || null,
        }
      }

      let responseFilters = null;
      if (Object.keys(newFilters).length === 0 || newTab.details === null) {
        responseFilters = newState.filters;
      }

      setActiveTab(prevState => {
        let tabFilters = responseFilters ? responseFilters : args.applyTabs ? newFilters : prevState.details;
        return {
          ...prevState,
          details: tabFilters,
          aggregates: aggregateCalcs,
          loaded: true
        }
      });

      setReportsOnTabs(prevState =>
        prevState.map(tab => {
          if (responseFilters) {
            return {
              ...tab,
              details: responseFilters,
              aggregates: aggregateCalcs,
              loaded: true
            }
          } else {
            let filtersSame = args.applyTabs ? areFiltersEqual(tab.details, newFilters) : areFiltersEqual(tab.details, newTab.details)
            return (tab.id === newTab.id || args.applyTabs === 'applyToAllTabs' || filtersSame)
              ? {
                ...tab,
                details: args.applyTabs ? newFilters : tab.details,
                aggregates: aggregateCalcs,
                loaded: true
              }
              : tab
          }
        })
      );

      setAllReportTabs(prevState =>
        prevState.map(tab => {
          if (responseFilters) {
            return {
              ...tab,
              details: responseFilters,
              aggregates: aggregateCalcs,
              loaded: true
            }
          } else {
            let filtersSame = args.applyTabs ? areFiltersEqual(tab.details, newFilters) : areFiltersEqual(tab.details, newTab.details)
            return (tab.id === newTab.id || args.applyTabs === 'applyToAllTabs' || filtersSame)
              ? {
                ...tab,
                details: args.applyTabs ? newFilters : tab.details,
                aggregates: aggregateCalcs,
                loaded: true
              }
              : tab
          }
        })
      );


      setState(prevState => {
        const updatedAnnualCalcs = mergeYouCalcs(prevState.calcs, calcs);
        const updatedMonthlyCalcs = mergeYouCalcs(prevState.monthly_calcs, monthly_calcs);
        const updatedT12Calcs = mergeYouCalcs(prevState.trailing12, trailing12);
        let newForecasts = { ...prevState.forecastCalcs, ...formattedMonthlyForecast };
        return {
          ...prevState,
          ...newState,
          calcs: updatedAnnualCalcs,
          monthly_calcs: updatedMonthlyCalcs,
          trailing12: updatedT12Calcs,
          default_filters: getDefaultFilters ? newState.default_filters : prevState.default_filters,
          forecastCalcs: newForecasts,
          trendData: newTrendData,
          filterOptions: finalFilterOptions,
          reportSpecific,
          loading: false,
          pnlTree: pnlTree,
          balTree: balTree
        }
      });

      if (calcsRes.companyReportSurvey) {
        let chartData = []
        let sectionDatas = Object.values(calcsRes.companyReportSurvey['survey_format_with_answers'])
        sectionDatas.forEach(data => {
          if (data.hasOwnProperty('sectionData')) chartData = [...chartData, ...data['sectionData']]
        })
        surveyCharts = chartData

        setSurveyData(() => {
          return {
            sections: calcsRes.companyReportSurvey['survey_format_with_answers']['sections'],
            surveyLayoutData: calcsRes.companyReportSurvey['survey_format_with_answers'],
            surveyCharts: surveyCharts,
          }
        })
      }

    } catch (error) {
      console.log(error, 'error')
      setState(prevState => {
        return {
          ...prevState,
          loading: false
        }
      });
      toast.error('Sorry something went wrong. Please try again later.', {
        position: 'top-right',
        autoClose: 5000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: false,
        progress: undefined,
      });
    }
  }

  const fetchMonthlyCalcs = async (args, { report, ...prevState }, isReportBuilderTabActive) => {
    // save any checked metrics and dashboard changes
    if (reportMetrics.editMetrics) { handleEditMetrics() }
    let newTab = args.newTab || activeTab;
    const newFilters = args.filters || {};
    let previousFilterOptions = []
    let previousNaicsCode = []
    let previousRegion = []
    let previousRevenueBand = []
    let previousYear = [];

    if (prevState.filterOptions && Object.keys(prevState.filterOptions).length > 0) {
      previousFilterOptions = prevState.filterOptions;
      previousNaicsCode = previousFilterOptions.code;
      previousRegion = previousFilterOptions.region;
      previousRevenueBand = previousFilterOptions.revenue_band;
      previousYear = previousFilterOptions.year;
    }

    let knownYears = trendData?.['total_revenue'] ? Object.keys(trendData['total_revenue']) : []
    let getDefaultFilters = Object.keys(state?.default_filters).length === 0
    let tabsToUpdate = tabsToUpdateCheck(args.applyTabs, newTab.id, newFilters)

    try {
      const [calculations, dashBoard, customReports] = await Promise.all([
        fetch('/api/v1/monthly_calculations', {
          method: 'POST',
          headers: {
            'X-CSRF-Token': window.token,
            'Accept': 'application/json',
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            company_id: props.id,
            known_years: knownYears,
            tabsToUpdate: tabsToUpdate,
            get_default_filters: getDefaultFilters,
            filters: newFilters,
            report,
            ...args // over write current state with new args
          }),
        }),
        !dashboardViewModalState.loaded
          ? fetch(`/get_dashboard_view/?report_type=${'money'}`, {
            headers: {
              'X-CSRF-Token': window.token,
            }
          })
          : Promise.resolve(null),
        isReportBuilderTabActive
          ? fetch(`/get_custom_reports`, {
            headers: {
              'X-CSRF-Token': window.token,
            }
          })
          : Promise.resolve(null)
      ]);

      const calcsRes = await calculations.json();
      const dashBoardRes = !dashboardViewModalState.loaded ? await dashBoard.json() : null;
      const customReportsRes = customReports ? await customReports.json() : null;
      const { reportSpecific, filterOptions, calcs, monthly_calcs, trailing12, ...newState } = calcsRes;
      let formattedMonthlyForecast = {}
      if (monthly_calcs?.forecast_default) {
        formattedMonthlyForecast = monthlyForecastFormatter(monthly_calcs?.forecast_default, newFilters.compare_with)
      }
      let currentDash = !dashboardViewModalState.loaded ? dashBoardRes['current_view'] : dashboardViewModalState.current_view
      let newTrendData = mergeMonthlyData(trendData, newState.trendData);
      const finalFilterOptions = {
        compare_with: filterOptions.compare_with,
        display_columns_by: filterOptions.display_columns_by,
      };
      if (filterOptions['number_of_pros']) { finalFilterOptions['number_of_pros'] = filterOptions['number_of_pros'] }
      let finalNaicsCode, finalRegion, finalRevenueBand, finalYear;
      let naicsCode = filterOptions.code;
      let region = filterOptions.region;
      let revenueBand = filterOptions.revenue_band;
      let year = filterOptions.year;


      if (Object.keys(prevState.filterOptions).length > 0) {
        let mergedNaicsCodes = [...previousNaicsCode, ...naicsCode]
        let previousCodeNumbers = previousNaicsCode.map((n) => n[0])
        let newCodeNumbers = naicsCode.map((n) => n[0])
        let allNaicsCodeNumbers = previousCodeNumbers.concat(newCodeNumbers.filter((n) => !previousCodeNumbers.includes(n)))
        let sortedNaicsCodes = allNaicsCodeNumbers.sort()
        finalNaicsCode = sortedNaicsCodes.map((n) => {
          let naicsCode = mergedNaicsCodes.find((mc) => mc[0] === n)
          return naicsCode
        })
        finalRegion = previousRegion.concat(region.filter((r) => !previousRegion.includes(r)))
        finalRevenueBand = previousRevenueBand.concat(revenueBand.filter((rb) => !previousRevenueBand.includes(rb)))
        finalYear = previousYear.concat(year.filter((y) => !previousYear.includes(y)))
        finalFilterOptions.code = finalNaicsCode
        finalFilterOptions.region = finalRegion;
        finalFilterOptions.revenue_band = finalRevenueBand;
        finalFilterOptions.year = finalYear;
      } else {
        finalFilterOptions.code = naicsCode;
        finalFilterOptions.region = region;
        finalFilterOptions.revenue_band = revenueBand;
        finalFilterOptions.year = year;
      }

      let reportBuilderCurrentDash = {
        selected_items: {
          money_all_selected_items: reportBuilderMetrics['allSelectedMetrics'],
          rev_and_opex_charts: reportBuilderMetrics['rev_and_opex_charts'],
          peerview_charts: reportBuilderMetrics['peerview_charts'],
          pnl: reportBuilderMetrics['pnl'],
          balance_sheet: reportBuilderMetrics['balance_sheet'],
          cash_flow: reportBuilderMetrics['cash_flow'],
          kfis: reportBuilderMetrics['kfis'],
          people_peerview_charts: reportBuilderMetrics['people_peerview_charts'],
          people_financial_detail: reportBuilderMetrics['people_financial_detail'],
          people_kpi: reportBuilderMetrics['people_kpi'],
          customers_peerview_charts: reportBuilderMetrics['customers_peerview_charts'],
          customers_financial_detail: reportBuilderMetrics['customers_financial_detail'],
          customers_kpi: reportBuilderMetrics['customers_kpi'],
        },
        dashboard_layout: reportBuilderMetrics['layout']
      }
      if (customReportsRes && customReportsRes.current_view?.selected_items && customReportsRes.current_view?.selected_items?.peerview_charts) {
        setReportBuilderModalState(prevState => {
          return {
            ...prevState,
            ...customReportsRes,
            calledAlready: true
          }
        })
        reportBuilderCurrentDash = customReportsRes.current_view
      }
      buildDashboardMetrics('report_builder', customReportsRes, newState.tree, reportBuilderCurrentDash, setReportBuilderMetrics, setReportBuilderModalState)

      if ((report === 'money' || report === 'analysis') && dashBoardRes) {
        buildDashboardMetrics('dashboard', dashBoardRes, newState.tree, currentDash, setReportMetrics, setDashboardViewModalState)
        setMoneyNotes(prevState => {
          return {
            userNotes: dashBoardRes['money_notes'] ? dashBoardRes['money_notes'] : prevState.userNotes
          }
        })
      } else {
        setDashboard(() => {
          return {
            id: currentDash.id,
            selectedItems: currentDash.selected_items,
            layout: currentDash.dashboard_layout,
            noChangesMade: true
          }
        })

        setDashboardViewModalState((prevState) => {
          return {
            ...prevState,
            current_view: currentDash,
            user_views: dashBoardRes ? dashBoardRes?.user_views : prevState.user_views,
            firms_views: dashBoardRes ? dashBoardRes?.firms_views : prevState.firms_views
          }
        })
      }

      let aggregateCalcs = {
        annual: {
          avg: calcs?.avg || null,
          docs_avg: calcs?.docs_avg || null,
          practice_avg: calcs?.practice_avg || null,
          nschbc: calcs?.nschbc || null,
          top_10_revenue_growth: calcs?.top_10_revenue_growth || null,
          top_25_revenue_growth: calcs?.top_25_revenue_growth || null,
          top_10_net_profit: calcs?.top_10_net_profit || null,
          top_25_net_profit: calcs?.top_25_net_profit || null,
        },
        monthly: {
          avg: monthly_calcs?.avg || null,
          docs_avg: monthly_calcs?.docs_avg || null,
        },
        trailing12: {
          avg: trailing12?.avg || null,
          docs_avg: trailing12?.docs_avg || null,
        },
        monthly_avg_n: newState?.monthly_avg_n || null,
        monthly_docs_avg_n: newState?.monthly_docs_avg_n || null
      }

      let responseFilters = null;
      if (Object.keys(newFilters).length === 0 || newTab.details === null) {
        responseFilters = newState.filters;
      }

      setActiveTab(prevState => {
        let tabFilters = responseFilters ? responseFilters : args.applyTabs ? newFilters : prevState.details;
        return {
          ...prevState,
          details: tabFilters,
          aggregates: aggregateCalcs,
          loaded: true
        }
      });

      setReportsOnTabs(prevState =>
        prevState.map(tab => {
          if (responseFilters) {
            return {
              ...tab,
              details: responseFilters,
              aggregates: aggregateCalcs,
              loaded: true
            }
          } else {
            let filtersSame = args.applyTabs ? areFiltersEqual(tab.details, newFilters) : areFiltersEqual(tab.details, newTab.details)
            return (tab.id === newTab.id || args.applyTabs === 'applyToAllTabs' || filtersSame)
              ? {
                ...tab,
                details: args.applyTabs ? newFilters : tab.details,
                aggregates: aggregateCalcs,
                loaded: true
              }
              : tab
          }
        })
      );

      setAllReportTabs(prevState =>
        prevState.map(tab => {
          if (responseFilters) {
            return {
              ...tab,
              details: responseFilters,
              aggregates: aggregateCalcs,
              loaded: true
            }
          } else {
            let filtersSame = args.applyTabs ? areFiltersEqual(tab.details, newFilters) : areFiltersEqual(tab.details, newTab.details)
            return (tab.id === newTab.id || args.applyTabs === 'applyToAllTabs' || filtersSame)
              ? {
                ...tab,
                details: args.applyTabs ? newFilters : tab.details,
                aggregates: aggregateCalcs,
                loaded: true
              }
              : tab
          }
        })
      );

      setState(prevState => {
        const updatedAnnualCalcs = mergeYouCalcs(prevState.calcs, calcs);
        const updatedMonthlyCalcs = mergeYouCalcs(prevState.monthly_calcs, monthly_calcs);
        const updatedT12Calcs = mergeYouCalcs(prevState.trailing12, trailing12);
        let newForecasts = { ...prevState.forecastCalcs, ...formattedMonthlyForecast }
        return {
          ...prevState,
          ...newState,
          calcs: updatedAnnualCalcs,
          monthly_calcs: updatedMonthlyCalcs,
          trailing12: updatedT12Calcs,
          default_filters: getDefaultFilters ? newState.default_filters : prevState.default_filters,
          forecastCalcs: newForecasts,
          trendData: newTrendData,
          filterOptions: finalFilterOptions,
          reportSpecific,
          loading: false
        }
      })

    } catch (error) {
      console.log(error)
      setState(prevState => {
        return {
          ...prevState,
          loading: false
        }
      });
      toast.error('Sorry something went wrong. Please try again later.', {
        position: 'top-right',
        autoClose: 5000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: false,
        progress: undefined,
      });
    }
  }

  const fetchAllCalcsByTabs = async () => {
    let unloadedTabs = reportsOnTabs.filter(tab => !tab.loaded)
    if (unloadedTabs.length > 0) {
      try {
        setState(prevState => {
          return {
            ...prevState,
            loading: true
          }
        })

        const res = await fetch(`/api/v1/calculations/all_calcs_by_tab`, {
          method: 'POST',
          headers: {
            'X-CSRF-Token': window.token,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            company_id: props.id,
            tabs: unloadedTabs
          }),
        })

        const resJson = await res.json();

        if (!res.ok) {
          throw new Error('Network response was not ok.');
        } else {
          setState(prevState => {
            const updatedAnnualCalcs = mergeYouCalcs(prevState.calcs, resJson.calcs);
            const updatedMonthlyCalcs = mergeYouCalcs(prevState.monthly_calcs, resJson.monthly_calcs);
            const updatedT12Calcs = mergeYouCalcs(prevState.trailing12, resJson.trailing12);
            let newForecasts = { ...prevState.forecastCalcs }

            if (resJson?.monthly_forecast_scenarios) {
              let formattedMonthlyForecast = {}
              let forecastNames = Object.keys(resJson?.monthly_forecast_scenarios || {});
              forecastNames.forEach(fName => {
                formattedMonthlyForecast = monthlyForecastFormatter(resJson?.monthly_forecast_scenarios[fName], fName)
                newForecasts = { ...newForecasts, ...formattedMonthlyForecast };
              })
            }

            return {
              ...prevState,
              companyYTDDate: resJson.companyYTDDate, 
              aggregateYTDDate: resJson.aggregateYTDDate,
              calcs: updatedAnnualCalcs,
              monthly_calcs: updatedMonthlyCalcs,
              trailing12: updatedT12Calcs,
              forecastCalcs: newForecasts,
              loading: false
            }
          })

          const newTabs = {};

          setReportsOnTabs(prevState => {
            resJson.agg_calcs_by_tab.forEach(ac => {
              ac.ids.forEach(id => {
                newTabs[id] = {
                  details: ac.details,
                  aggregates: ac.data,
                  loaded: true
                };
              });
            });

            return prevState.map(tab => {
              if (newTabs[tab.id]) {
                return {
                  ...tab,
                  ...newTabs[tab.id]
                };
              }
              return tab;
            });
          });

          setAllReportTabs(prevState => {
            return prevState.map(tab => {
              if (newTabs[tab.id]) {
                return {
                  ...tab,
                  ...newTabs[tab.id]
                };
              }
              return tab;
            });
          });
        }
      } catch (error) {
        console.log(error)
        alert('Sorry something went wrong. Data for all tabs was not gathered properly.');
      }
    }
  }

  async function submitFilters(event, filters) {
    event.preventDefault();
    let applyTabs = event.nativeEvent.submitter.name;
    let allFilters = {
      filters: {
        code: filters.code,
        compare_with: filters.compare_with,
        datatype: filters.datatype,
        display_columns_by: filters.display_columns_by,
        entity_type: filters.entity_type,
        number_of_pros: filters.number_of_pros,
        region: filters.region,
        revenue_band: filters.revenue_band,
        report_period: filters.report_period,
        year: filters.year,
        date_through_monthly_month: filters.date_through_monthly_month,
        date_through_monthly_year: filters.date_through_monthly_year,
      },
      applyTabs: applyTabs
    }
    updateState(allFilters);
  }

  async function updateState(args = {}, getCustomReports = false) {
    const isReportBuilderTabActive = getCustomReports && activeTab.key === 'report_builder';
    setState(prevState => ({
      ...prevState,
      report: args.report || prevState.report,
      loading: true
    }));

    const currentState = state;

    if (args.filters) {
      if (args.filters['display_columns_by'] === 'Years' && args.filters['report_period'] === 'Year to Date') {
        fetchMonthlyCalcs(args, currentState, isReportBuilderTabActive);
      } else if (args.filters['display_columns_by'] === 'Years' && args.filters['report_period'] !== 'Year to Date') {
        fetchCalcs(args, currentState, isReportBuilderTabActive);
      } else if (args.filters['display_columns_by'] === 'Months') {
        fetchMonthlyCalcs(args, currentState, isReportBuilderTabActive)
      } else {
        fetchCalcs(args, currentState, isReportBuilderTabActive);
      }
    } else {
      fetchCalcs(args, currentState, isReportBuilderTabActive);
    }

  };

  // fetch calculations on load if not present in props
  useEffect(() => {
    if (state.fetch) {
      let allFilters = {}
      let getCustomReports = true
      if (Object.keys(state.filters).length > 0) {
        allFilters.filters = state.filters
      }
      updateState(allFilters, getCustomReports);
      if (props.most_recent_fdu && props.most_recent_fdu.state === 'mappered') {
        toast(`New accounts need to be mapped for this client! Please map your recent file, ${props.most_recent_fdu.document}, in the Map Files page. Then click the Generate Reports button to update your client's reports.`)
      }
    };
  }, []);

  const saveChartStyleEdits = () => {
    setEditChartModel(prevState => {
      return {
        ...prevState,
        isOpen: false
      }
    })

    if (activeTab.key === 'report_builder') {
      updateCustomReport()
    } else {
      updateDashBoardView()
    }
  }

  const handleReportViewEditModal = (action) => {
    if (action === 'edit') {
      let templateInfo = templateReportView?.template_report_view;
      setReportViewEditIsOpen(true)
      setReportViewForm(prevState => {
        return {
          ...prevState,
          ...templateInfo,
          naicsSpecific: templateInfo.naics && templateInfo.naics.length > 0 ? 'yes' : 'no',
          parentSpecific: ((templateReportView && templateReportView?.child_companies?.length > 0) || (templateReportView && templateReportView?.parent_companies?.length > 0)) ? 'yes' : 'no',
          naicsSearch: templateInfo.naics,
          parentSearch: templateReportView.parent_companies,
          companySearch: templateReportView.child_companies,
          userType: templateInfo.admin_default ? 'admin' : 'normal',
          action: action
        }
      })
    } else if (action === 'create') {
      setReportViewEditIsOpen(true)
      setReportViewForm(prevState => {
        return {
          ...prevState,
          action: action
        }
      })
    } else if (action === 'close') {
      setReportViewEditIsOpen(false)
    } else if (action === 'admin' || action === 'normal') {
      setReportViewForm(prevState => {
        return {
          ...prevState,
          userType: action
        }
      })
    }
  }

  const handleEditChartModal = (action, chartDetails = {}, highChartOptions = {}) => {
    if (action === 'close') {
      // CHECK CUSTOMERS
      if (state.report === 'money' || state.report === 'analysis' || state.report === 'people' || state.report === 'customers') {
        if (activeTab.key === 'report_builder') {
          setReportBuilderMetrics((prevState) => {
            let oldLayout = _.cloneDeep(prevState.layout)
            // Save old styles to parent
            let newLayout = oldLayout.map(parentItem => {
              if (parentItem.i === editChartModel.chartDetails.parentKey || parentItem.i === editChartModel.chartDetails.i) {
                parentItem['metricRow']['styles'] = editChartModel.oldStyles
              }
              return parentItem
            })

            return {
              ...prevState,
              layout: newLayout,
              noChangesMade: false
            }
          })
        } else {
          setReportMetrics((prevState) => {
            let oldLayout = _.cloneDeep(prevState.layout)
            // Save old styles to parent
            let newLayout = oldLayout.map(parentItem => {
              if (parentItem.i === editChartModel.chartDetails.parentKey || parentItem.i === editChartModel.chartDetails.i) {
                parentItem['metricRow']['styles'] = editChartModel.oldStyles
              }
              return parentItem
            })

            return {
              ...prevState,
              layout: newLayout,
              noChangesMade: false
            }
          })
        }
      } else {
        setDashboard((prevState) => {
          let oldLayout = _.cloneDeep(prevState.layout)
          let newLayout = oldLayout.map(parentItem => {
            if (parentItem.i === editChartModel.chartDetails.i) {
              parentItem['styles'] = editChartModel.oldStyles
            }
            return parentItem
          })

          return {
            ...prevState,
            layout: newLayout,
            noChangesMade: false
          }
        })
      }

      setEditChartModel(prevState => {
        return {
          ...prevState,
          isOpen: false
        }
      })
    } else {
      setEditChartModel(prevState => {
        return {
          ...prevState,
          isOpen: true,
          chartDetails: chartDetails,
          highChartOptions: highChartOptions,
          oldStyles: chartDetails.styles,
          newStyles: chartDetails.styles
        }
      })
    }
  }

  const handleEditChartStyle = (seriesOneStyle, seriesTwoStyle) => {
    let newChartStyles;
    setEditChartModel(prevState => {
      newChartStyles = _.cloneDeep(editChartModel.newStyles);
      let newHighChartOptions = _.cloneDeep(editChartModel.highChartOptions)
      newChartStyles['seriesOne']['type'] = seriesOneStyle
      newChartStyles['seriesTwo']['type'] = seriesTwoStyle
      newHighChartOptions['series'][0]['type'] = seriesOneStyle
      if (newHighChartOptions['series'][1]) {
        newHighChartOptions['series'][1]['type'] = seriesTwoStyle
      }

      return {
        ...prevState,
        highChartOptions: newHighChartOptions,
        newStyles: newChartStyles
      }
    })

    if (state.report === 'money' || state.report === 'analysis' || state.report === 'people' || state.report === 'customers') {
      if (activeTab.key === 'report_builder') {
        setReportBuilderMetrics((prevState) => {
          let oldLayout = _.cloneDeep(prevState.layout)
          // Save styles to parent
          let newLayout = oldLayout.map(parentItem => {
            if (parentItem.i === editChartModel.chartDetails.parentKey || parentItem.i === editChartModel.chartDetails.i) {
              parentItem['metricRow']['styles'] = newChartStyles
            }
            return parentItem
          })
  
          return {
            ...prevState,
            layout: newLayout,
            noChangesMade: false
          }
        })
      } else {
        setReportMetrics((prevState) => {
          let oldLayout = _.cloneDeep(prevState.layout)
          // Save styles to parent
          let newLayout = oldLayout.map(parentItem => {
            if (parentItem.i === editChartModel.chartDetails.parentKey || parentItem.i === editChartModel.chartDetails.i) {
              parentItem['metricRow']['styles'] = newChartStyles
            }
            return parentItem
          })
  
          return {
            ...prevState,
            layout: newLayout,
            noChangesMade: false
          }
        })
      }
    } else {
      setDashboard((prevState) => {
        let oldLayout = _.cloneDeep(prevState.layout)
        let newLayout = oldLayout.map(parentItem => {
          if (parentItem.i === editChartModel.chartDetails.i) {
            parentItem['styles'] = newChartStyles
          }
          return parentItem
        })

        return {
          ...prevState,
          layout: newLayout,
          noChangesMade: false
        }
      })
    }
  }

  const buildEditChartModel = () => {
    let style1 = editChartModel.newStyles['seriesOne']['type']
    let style2 = editChartModel.newStyles['seriesTwo']['type']

    return (
      <div className='edit-chart-modal-container'>
        <HighchartsReact containerProps={{ style: { width: '65%', height: '65%' }, key: 'edit-chart-in-modal', id: 'edit-chart-in-modal' }} highcharts={Highcharts} options={editChartModel.highChartOptions} />
        <div className='edit-chart-actions-container'>
          <h3 className='edit-chart-title'>Edit Chart Style</h3>
          <div className='edit-chart-style-options'>
            <div className={style1 === 'line' & style2 === 'line' ? 'active' : ''} onClick={() => handleEditChartStyle('line', 'line')}><LinesChart report={report} /> Lines</div>
            <div className={style1 === 'column' & style2 === 'column' ? 'active' : ''} onClick={() => handleEditChartStyle('column', 'column')}><ColumnsChart report={report} /> Columns</div>
            <div className={style1 === 'line' & style2 === 'column' ? 'active' : ''} onClick={() => handleEditChartStyle('line', 'column')}><LineColChart report={report} /> Line / Column</div>
            <div className={style1 === 'column' & style2 === 'line' ? 'active' : ''} onClick={() => handleEditChartStyle('column', 'line')}><ColLineChart report={report} /> Column / Line</div>
          </div>
          <div className='edit-chart-modal-btns'>
            <button className='btn btn-primary' onClick={() => saveChartStyleEdits()}>Save Changes</button>
            <button className='btn btn-danger' onClick={() => handleEditChartModal('close')}>Cancel</button>
          </div>
        </div>
      </div>
    )
  }

  const handleMetricNotes = (parentKey, text) => {
    if (activeTab.key === 'report_builder') {
      setReportBuilderMetrics((prevState) => {
        let oldLayout = _.cloneDeep(prevState.layout)
        // Save notes to parent
        let newLayout = oldLayout.map(parentItem => {
          if (parentItem['metricRow'] && parentItem['metricRow']['relatedCheckedKey'] === parentKey) {
            parentItem['metricRow']['notes'] = text
          }
          return parentItem
        })

        return {
          ...prevState,
          layout: newLayout,
          noChangesMade: false
        }
      })
    } else {
      setReportMetrics((prevState) => {
        let oldLayout = _.cloneDeep(prevState.layout)
        // Save notes to parent
        let newLayout = oldLayout.map(parentItem => {
          if (parentItem['metricRow'] && parentItem['metricRow']['relatedCheckedKey'] === parentKey) {
            parentItem['metricRow']['notes'] = text
          }
          return parentItem
        })

        return {
          ...prevState,
          layout: newLayout,
          noChangesMade: false
        }
      })
    }
  }

  const openFiltersModal = () => {
    setFiltersModalIsOpen(true)
  }

  const closeFiltersModal = () => {
    setFiltersModalIsOpen(false)
  }

  const handleNewTabFormModal = (reportDetails) => {
    setTabNameModalOpen(true)
    setTabNameForm(prevState => {
      return {
        ...prevState,
        key: reportDetails.key,
        report: reportDetails.report,
        reportType: reportDetails.name,
        name: reportDetails.name
      }
    })
  }

  const closeNewTabFormModal = () => {
    setTabNameModalOpen(false)
  }

  const handleNewTabName = (e) => {
    let name = e.target.value;
    if (name.length === 0) {
      setTabNameForm(prevState => {
        return {
          ...prevState, error: 'Please enter a name for the new tab.'
        }
      })
    } else {
      setTabNameForm(prevState => {
        return {
          ...prevState,
          error: null,
          name: name
        }
      })
    }
  }

  const handleAskPeerviewAccess = () => {
    if (navbar.user_type === 'Admin' || (navbar.reports_unlocked && props.planAccess.money.ai)) {
      setCopilotOpen(true)
    } else {
      setVideoModalOpen(true)
    }
  }

  const hrefBuilder = (subdomain, path) => {
      const domainSplit = document.location.host.split('.');
      domainSplit[0] = subdomain;
      return `${location.protocol}//${domainSplit.join('.')}${path}`;
  };

  const VideoModal = () => {
    let modalPic = '';
    let modalTitle = '';
    let videoLink = null;
    let message = '';
    const manageSubscriptionsURL = hrefBuilder(navbar?.subdomain, '/subscriptions');
    const unlockReportsURL = hrefBuilder(navbar?.subdomain, navbar?.unlock_reports_url);
    const linkText = navbar?.report_credits ? 'Unlock Reports' : 'Buy More Report Credits';
    const purchaseText = 'Buy more report credits.';
    const aiAccess = props?.planAccess?.money?.ai

    if (!aiAccess && navbar?.user_type !== 'Admin') {
      message = 'AI features are not included in your current plan. Please upgrade your plan for access to AI features and unlock report with a report credit.'
    } else if (!navbar?.reports_unlocked && aiAccess && navbar?.user_type !== 'Admin') {
      message = 'Please unlock report with a report credit to access AI features for this report.'
    }

    return (
        <Modal
        className={{
            base: 'report-credit-modal-content',
            afterOpen: `report-credit-modal-content_after-open hide-on-print`,
            beforeClose: `report-credit-modal-content_before-close hide-on-print`,
        }}
        overlayClassName={{
            base: 'overlay-base hide-on-print',
            afterOpen: 'overlay-base_after-open hide-on-print',
            beforeClose: 'overlay-base_before-close hide-on-print',
        }}
        isOpen={videoModalOpen}
        onRequestClose={() => setVideoModalOpen(false)}
        shouldCloseOnOverlayClick
        closeTimeoutMS={0}
        contentLabel="Unlocking Report Features with Report Credits"
        ariaHideApp={false}
        >
          <div>
              <h1 style={{ 'fontSize': '18px'}}>{message}</h1>
              <div className="unlock-report-container">
                  <p>
                  {`You have ${navbar?.report_credits} Report Credits left.`}{' '}
                  <a className="btn btn-primary unlock-report-btn" href={unlockReportsURL}>
                      {linkText}
                  </a>
                  </p>
              </div>
              <a className="btn btn-primary manage-subscription-btn" href={manageSubscriptionsURL}>
                  Manage Your Subscription
              </a>
              <p>Click anywhere outside to close this pop-up.</p>
          </div>
        </Modal>
    );
  }

  const { loading, report, pageTitle, clientName, realName, entity_type, calendarYearEnd, fiscalYearStartMonth, peerviewMetricsYears, calcs, monthly_calcs, trailing12, ytd_months, baseCalc, forecastCalcs, tree, pnlTree, balTree, yearRange, n, docs_n, filters, filterOptions, insufficient_data, insufficient_peer_data, reportSpecific, isMonthlyAvailable, quarterMonths, trendData, fiscalYearEnd, companyYTDDate, aggregateYTDDate, companyReportSurvey, surveyTaken, surveyResults } = state;

  return (
    <div className={`${report}-content report-content`}>
      <ToastContainer
        position='top-right'
        autoClose={30000}
        hideProgressBar
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable={false}
        theme='colored'
        pauseOnHover
      />
      <VideoModal />
      <Modal
        className={{
          base: 'client-modal-content',
          afterOpen: 'client-modal-content_after-open',
          beforeClose: 'client-modal-content_before-close',
        }}
        overlayClassName={{
          base: 'overlay-base',
          afterOpen: 'overlay-base_after-open',
          beforeClose: 'overlay-base_before-close',
        }}
        isOpen={filtersModalIsOpen}
        onRequestClose={closeFiltersModal}
        shouldCloseOnOverlayClick={true}
        closeTimeoutMS={0}
        contentLabel='Report Filters Modal'
      >
        <div className='money-dropdown-container'>
          <header className='company-modal-header'>
            <h1 className='client-company-name'>Report Filters</h1>
          </header>
          <div className='report-filters-container'>
            <div className='client-info-div'>
              {props.client_name ? <p>CLIENT ID: {props.client_name}</p> : null}
              {props.real_name ? <p>CLIENT NAME: {props.real_name}</p> : null}
              <p>FISCAL YEAR END: {props.fiscal_year_end}</p>
              <p>NAICS: {props.code}, {props.industry}</p>
            </div>
            <DropdownFilter
              clas={'money-dropdown-filters'}
              calendarYearEnd={calendarYearEnd}
              current={filters}
              activeTab={activeTab}
              getMonthlyForecastScenario={getMonthlyForecastScenario}
              isMonthlyAvailable={isMonthlyAvailable}
              monthlyCompareWithOptions={state.monthlyCompareWithOptions}
              naicsCode={props.code}
              options={filterOptions}
              youAnnualOptions={state.youAnnualOptions}
              youMonthlyOptions={state.youMonthlyOptions}
              peerAnnualOptions={state.peerAnnualOptions}
              peerMonthlyOptions={state.peerMonthlyOptions}
              onClick={submitFilters}
              report={'money'}
            />
          </div>
        </div>
      </Modal>
      <Modal
        className={{
          base: 'edit-chart-modal-content hide-on-print',
          afterOpen: 'edit-chart-modal-content_after-open hide-on-print',
          beforeClose: 'edit-chart-modal-content_before-close hide-on-print',
        }}
        overlayClassName={{
          base: 'edit-chart-overlay-base hide-on-print',
          afterOpen: 'edit-chart-overlay-base_after-open hide-on-print',
          beforeClose: 'edit-chart-overlay-base_before-close hide-on-print',
        }}
        ariaHideApp={false}
        closeTimeoutMS={0}
        contentLabel='Add / Edit Report View Modal'
        isOpen={reportViewEditIsOpen}
        onRequestClose={() => setReportViewEditIsOpen(false)}
        shouldCloseOnOverlayClick={false}
      >
        <ReportViewPopup
          userType={navbar.user_type}
          reportViewForm={reportViewForm}
          createReportView={createReportView}
          updateReportView={updateReportView}
          archiveReportView={archiveReportView}
          setReportViewEditIsOpen={setReportViewEditIsOpen}
          setReportViewForm={setReportViewForm}
          handleReportViewEditModal={handleReportViewEditModal}
        />
      </Modal>
      <Modal
        className={{
          base: 'new-report-tab-content',
          afterOpen: 'new-report-tab-content_after-open',
          beforeClose: 'new-report-tab-content_before-close',
        }}
        overlayClassName={{
          base: 'new-report-tab-overlay-base',
          afterOpen: 'new-report-tab-overlay-base_after-open',
          beforeClose: 'new-report-tab-overlay-base_before-close',
        }}
        isOpen={tabNameModelIsOpen}
        onRequestClose={closeNewTabFormModal}
        shouldCloseOnOverlayClick={false}
        closeTimeoutMS={0}
        contentLabel='Add New Tab Name Modal'
      >
        <div className='new-report-tab-container'>
          <h1 className='company-modal-header'>Add a New Tab</h1>
          <form id='new-report-tab-form' onSubmit={(e) => createNewReportTab(e)}>
            <p>Report Type: {tabNameForm.reportType}</p>
            <p className='error-message'>{tabNameForm.error}</p>
            <div>
              <label>New Tab Name:</label>
              <input value={tabNameForm.name} onChange={(e) => handleNewTabName(e)} />
            </div>
            <button className='run-report-btn' type='submit'>Create New Tab</button>
          </form>
          <button className='run-report-btn' type='button' onClick={() => closeNewTabFormModal()}>CANCEL</button>
        </div>
      </Modal>
      <Modal
        className={{
          base: 'edit-chart-modal-content hide-on-print',
          afterOpen: 'edit-chart-modal-content_after-open hide-on-print',
          beforeClose: 'edit-chart-modal-content_before-close hide-on-print',
        }}
        overlayClassName={{
          base: 'edit-chart-overlay-base hide-on-print',
          afterOpen: 'edit-chart-overlay-base_after-open hide-on-print',
          beforeClose: 'edit-chart-overlay-base_before-close hide-on-print',
        }}
        ariaHideApp={false}
        closeTimeoutMS={0}
        contentLabel='Edit Dashboard Item Modal'
        isOpen={editChartModel.isOpen}
        onRequestClose={() => handleEditChartModal('close')}
        shouldCloseOnOverlayClick={false}
      >
        {buildEditChartModel()}
      </Modal>
      <DashboardViewModal
        dashboardViewModalState={dashboardViewModalState}
        report={report}
        handleDashboardViewModal={handleDashboardViewModal}
        createDashBoardView={createDashBoardView}
        deleteDashBoardView={deleteDashBoardView}
        updateDashBoardView={updateDashBoardView}
      />
      {calcs || monthly_calcs || trailing12 ?
        <Data.Provider value={{ peerviewMetricsYears, calcs, monthly_calcs, trailing12, ytd_months, baseCalc, forecastCalcs }} >
          <Filters.Provider value={{ filters, filterOptions }} >
            {props.children({
              ...reportSpecific,
              userId: props.user_id,
              templateReportView: templateReportView?.template_report_view,
              activeReportView,
              activeTab,
              reportsOnTabs,
              allReportTabs,
              defaultDashboardViews,
              customDashboardViews,
              customLayouts,
              loading: loading,
              entityType: entity_type,
              companyYTDDate: companyYTDDate,
              aggregateYTDDate: aggregateYTDDate,
              fiscalYearEnd: fiscalYearEnd,
              clientName: clientName,
              realName: realName,
              calendarYearEnd: calendarYearEnd,
              fiscalYearStartMonth: fiscalYearStartMonth,
              n: n,
              docs_n: docs_n,
              name: props.name,
              id: props.id,
              insufficientData: insufficient_data,
              insufficientPeerData: insufficient_peer_data,
              page: report,
              pageTitle,
              year: filters.year,
              yearRange: yearRange,
              tree,
              pnlTree,
              balTree,
              reportMetrics,
              calcs: calcs,
              quarterMonths: quarterMonths,
              trendData: trendData,
              dashboard: dashboard,
              tourData: props.tour_data,
              companyReportSurvey: companyReportSurvey,
              subdomain: props.subdomain,
              companyNotes: companyNotes,
              onChangeNotes: onChangeNotes,
              surveyTaken: surveyTaken,
              surveyResults: surveyResults,
              surveyModalIsOpen,
              surveyData: surveyData,
              moneyNotes,
              editDashboard,
              dashboardViewModalState: dashboardViewModalState,
              companyForm,
              reportBuilderMetrics,
              reportBuilderModalState,
              tabNameModelIsOpen,
              reportTabsPopoverOpen,
              setReportTabsPopoverOpen,
              fetchAllCalcsByTabs,
              handleReportViewEditModal,
              handleReportBuilderLayoutChange,
              handleDefaultDashboardViewTab,
              hideReportFromTabs,
              addReportToTabs,
              deleteReportTab,
              handleNewTabFormModal,
              saveTabOrder,
              handleSurveyModal,
              handleDashboardSelections,
              handleSelectAll,
              handleCustomLayoutChange,
              handleOnLayoutChange,
              handleToggleDraggable,
              handleSurveySubmit,
              handleEditChartModal,
              handleEditMetrics,
              handleSelectAndLayout,
              handleMoneyNotes,
              handleMetricNotes,
              handleEditDashBoard,
              handleDashboardViewModal,
              getCustomReport,
              createCustomReport,
              deleteCustomReport,
              updateCustomReport,
              handleReportBuilderModal,
              handleReportBuilderEditMetrics,
              handleReportBuilderSelectAndLayout,
              updateCustomLayout,
              getDashBoardView,
              updateDashBoardView,
              openFiltersModal,
              chatLoader,
              allChats: allChats,
              chat: chat,
              chatLoading: chatLoading,
              changeChat,
              handleCopilotCall,
              copilotOpen,
              setCopilotOpen,
            })}
          </Filters.Provider>
        </Data.Provider>
        : <div style={{ 'display': 'flex', 'justifyContent': 'center', 'alignItems': 'center', 'height': '100vh' }} >
          <LoaderGraphic />
        </div>}
      {!copilotOpen && (
        <button
          className="copilot-floating-btn"
          onClick={() => handleAskPeerviewAccess()}
          title="Ask Peerview"
        >
          <i className="fa fa-comments" />
        </button>
      )}
      <CopilotSlideOut
        isOpen={copilotOpen}
        onClose={() => setCopilotOpen(false)}
        activeTab={activeTab}
        companyName={props.name}
        userType={navbar.user_type}
        setupProd={navbar.setup}
        page={report}
        filters={
          activeTab && activeTab.details && Object.keys(activeTab.details).length > 0
            ? {
                compare_with: activeTab.details.compare_with,
                code: activeTab.details.code,
                year: activeTab.details.year,
                region: activeTab.details.region,
                revenue_band: activeTab.details.revenue_band,
                number_of_pros: activeTab.details.number_of_pros,
                entity_type: activeTab.details.entity_type
              }
            : null
        }
      />
    </div>
  )
};

export default ReportContainer;